<template>
  <div class="admin-page">
    <div class="page-header">
      <div class="container">
        <h1 class="page-title">管理者ログイン</h1>
      </div>
    </div>
    
    <section class="section">
      <div class="container">
        <div v-if="!user" class="auth-form">
          <h2 class="form-title">ログイン</h2>
          
          <div v-if="loginError" class="error-message">
            {{ loginError }}
          </div>
          
          <form @submit.prevent="login">
            <div class="form-group">
              <label for="email">メールアドレス</label>
              <input 
                type="email" 
                id="email" 
                v-model="loginForm.email" 
                required 
                placeholder="admin@example.com"
              >
            </div>
            
            <div class="form-group">
              <label for="password">パスワード</label>
              <input 
                type="password" 
                id="password" 
                v-model="loginForm.password" 
                required 
                placeholder="••••••••"
              >
            </div>
            
            <button type="submit" class="btn btn-primary" :disabled="isLoading">
              {{ isLoading ? 'ログイン中...' : 'ログイン' }}
            </button>
          </form>
        </div>
        
        <div v-else class="admin-dashboard">
          <div class="dashboard-header">
            <h2 class="welcome-text">ようこそ、{{ user.email }} 様</h2>
            <button @click="logout" class="btn btn-outline">ログアウト</button>
          </div>
          
          <div class="admin-tabs">
            <button 
              class="tab-btn" 
              :class="{ active: activeTab === 'notices' }" 
              @click="activeTab = 'notices'"
            >
              <i class="fas fa-bullhorn"></i> お知らせ管理
            </button>
            <button
              class="tab-btn"
              :class="{ active: activeTab === 'users' }"
              @click="activeTab = 'users'"
            >
              <i class="fas fa-users"></i> ユーザー管理
            </button>
            <button
              class="tab-btn"
              :class="{ active: activeTab === 'messages' }"
              @click="activeTab = 'messages'"
            >
              <i class="fas fa-envelope"></i> メッセージ管理
            </button>
            <button
              class="tab-btn"
              :class="{ active: activeTab === 'enrollments' }"
              @click="activeTab = 'enrollments'"
            >
              <i class="fas fa-graduation-cap"></i> 申し込み管理
            </button>
            <button
              class="tab-btn"
              :class="{ active: activeTab === 'access' }"
              @click="activeTab = 'access'"
            >
              <i class="fas fa-cog"></i> アクセス設定
            </button>
            <button
              class="tab-btn"
              :class="{ active: activeTab === 'page-editor' }"
              @click="activeTab = 'page-editor'"
            >
              <i class="fas fa-edit"></i> ページ編集
            </button>
          </div>
          
          <div class="tab-content">
            <!-- お知らせ管理タブ -->
            <div v-show="activeTab === 'notices'" class="tab-pane notices-management">
              <h3 class="section-subtitle">お知らせ一覧</h3>
              
              <button @click="showAddNoticeForm = true" class="btn btn-success add-btn">
                <i class="fas fa-plus"></i> 新規お知らせ追加
              </button>
              
              <!-- 新規お知らせフォーム -->
              <div v-if="showAddNoticeForm" class="notice-form">
                <h4>新規お知らせ作成</h4>
                <form @submit.prevent="addNotice">
                  <div class="form-group">
                    <label for="notice-title">タイトル</label>
                    <input 
                      type="text" 
                      id="notice-title" 
                      v-model="noticeForm.title" 
                      required
                    >
                  </div>
                  
                  <div class="form-group">
                    <label for="notice-date">日付</label>
                    <input 
                      type="date" 
                      id="notice-date" 
                      v-model="noticeForm.date" 
                      required
                    >
                  </div>
                  
                  <div class="form-group">
                    <label for="notice-content">内容</label>
                    <textarea 
                      id="notice-content" 
                      v-model="noticeForm.content" 
                      rows="5" 
                      required
                    ></textarea>
                  </div>
                  
                  <div class="form-group">
                    <label for="notice-image">画像（任意）</label>
                    <input
                      type="file"
                      id="notice-image"
                      @change="handleImageChange"
                      accept="image/*"
                      :disabled="isUploadingImage"
                    >
                    <div v-if="uploadProgress > 0 && uploadProgress < 100" class="progress">
                      <div class="progress-bar" :style="{ width: uploadProgress + '%' }"></div>
                      <span>{{ uploadProgress }}%</span>
                    </div>
                    <div v-if="noticeForm.imageUrl" class="image-preview">
                      <img :src="noticeForm.imageUrl" alt="プレビュー">
                    </div>
                  </div>
                  
                  <div class="form-check">
                    <input 
                      type="checkbox" 
                      id="is-important" 
                      v-model="noticeForm.isImportant"
                    >
                    <label for="is-important">重要</label>
                  </div>
                  
                  <div class="form-actions">
                    <button type="button" @click="cancelAddNotice" class="btn btn-outline">
                      キャンセル
                    </button>
                    <button type="submit" class="btn btn-primary" :disabled="isSubmitting || isUploadingImage">
                      {{ isSubmitting ? '送信中...' : (isUploadingImage ? '画像処理中...' : '保存') }}
                    </button>
                  </div>
                </form>
              </div>
              
              <!-- お知らせ一覧 -->
              <div class="notices-list-admin">
                <div v-if="isLoadingNotices" class="loading-state">
                  読み込み中...
                </div>
                <div v-else-if="notices.length === 0" class="empty-state">
                  お知らせはありません。
                </div>
                <div v-else class="notice-items">
                  <div 
                    v-for="notice in notices" 
                    :key="notice.id" 
                    class="notice-item"
                  >
                    <div class="notice-item-header">
                      <div class="notice-item-title">
                        {{ notice.title }}
                        <span v-if="notice.isImportant" class="important-badge">重要</span>
                      </div>
                      <div class="notice-item-date">{{ formatDate(notice.date) }}</div>
                    </div>
                    <div class="notice-item-content" v-html="notice.content"></div>
                    <div class="notice-item-actions">
                      <button @click="editNotice(notice)" class="btn btn-sm btn-outline">
                        編集
                      </button>
                      <button @click="confirmDeleteNotice(notice.id)" class="btn btn-sm btn-danger">
                        削除
                      </button>
                    </div>
                  </div>
                </div>
              </div>
              
              <!-- 編集フォーム -->
              <div v-if="editingNotice" class="notice-form editing">
                <h4>お知らせ編集</h4>
                <form @submit.prevent="updateNotice">
                  <div class="form-group">
                    <label for="edit-notice-title">タイトル</label>
                    <input 
                      type="text" 
                      id="edit-notice-title" 
                      v-model="editNoticeForm.title" 
                      required
                    >
                  </div>
                  
                  <div class="form-group">
                    <label for="edit-notice-date">日付</label>
                    <input 
                      type="date" 
                      id="edit-notice-date" 
                      v-model="editNoticeForm.date" 
                      required
                    >
                  </div>
                  
                  <div class="form-group">
                    <label for="edit-notice-content">内容</label>
                    <textarea 
                      id="edit-notice-content" 
                      v-model="editNoticeForm.content" 
                      rows="5" 
                      required
                    ></textarea>
                  </div>
                  
                  <div class="form-group">
                    <label for="edit-notice-image">画像（任意）</label>
                    <input
                      type="file"
                      id="edit-notice-image"
                      @change="handleEditImageChange"
                      accept="image/*"
                      :disabled="isUploadingImage"
                    >
                    <div v-if="uploadProgress > 0 && uploadProgress < 100" class="progress">
                      <div class="progress-bar" :style="{ width: uploadProgress + '%' }"></div>
                      <span>{{ uploadProgress }}%</span>
                    </div>
                    <div v-if="editNoticeForm.imageUrl" class="image-preview">
                      <img :src="editNoticeForm.imageUrl" alt="プレビュー">
                      <button type="button" @click="removeImage" class="btn btn-sm btn-danger" :disabled="isUploadingImage">画像を削除</button>
                    </div>
                  </div>
                  
                  <div class="form-check">
                    <input 
                      type="checkbox" 
                      id="edit-is-important" 
                      v-model="editNoticeForm.isImportant"
                    >
                    <label for="edit-is-important">重要</label>
                  </div>
                  
                  <div class="form-actions">
                    <button type="button" @click="cancelEdit" class="btn btn-outline">
                      キャンセル
                    </button>
                    <button type="submit" class="btn btn-primary" :disabled="isSubmitting || isUploadingImage">
                       {{ isSubmitting ? '更新中...' : (isUploadingImage ? '画像処理中...' : '更新') }}
                    </button>
                  </div>
                </form>
              </div>
              
              <!-- 削除確認モーダル -->
              <div v-if="showDeleteModal" class="delete-modal">
                <div class="delete-modal-content">
                  <h4>削除確認</h4>
                  <p>このお知らせを削除してもよろしいですか？この操作は元に戻せません。</p>
                  <div class="delete-modal-actions">
                    <button @click="showDeleteModal = false" class="btn btn-outline">
                      キャンセル
                    </button>
                    <button @click="deleteNotice" class="btn btn-danger" :disabled="isDeleting">
                      {{ isDeleting ? '削除中...' : '削除' }}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <!-- User Management Tab -->
            <div v-show="activeTab === 'users'" class="tab-pane users-management">
              <h3 class="section-subtitle">登録ユーザー一覧</h3>
              
              <!-- 筛选器 -->
              <div class="users-filter">
                <div class="filter-item">
                  <label>ステータス:</label>
                  <select v-model="usersFilter.status" class="filter-select">
                    <option value="all">全て</option>
                    <option value="active">アクティブ</option>
                    <option value="banned">停止中</option>
                  </select>
                </div>
                <div class="filter-item">
                  <label>検索:</label>
                  <input 
                    type="text" 
                    v-model="usersFilter.search" 
                    placeholder="名前またはメールで検索"
                    class="filter-input"
                  >
                </div>
                <button @click="fetchUsers" class="btn btn-sm btn-outline refresh-btn">
                  <i class="fas fa-sync-alt"></i> 更新
                </button>
              </div>

              <div v-if="isLoadingUsers" class="loading-state">
                ユーザー情報を読み込み中...
              </div>
              <div v-else-if="usersError" class="error-state">
                {{ usersError }}
              </div>
              <div v-else-if="filteredUsers.length === 0" class="empty-state">
                ユーザーが見つかりません。
              </div>
              <div v-else class="users-list">
                <div 
                  v-for="user in filteredUsers" 
                  :key="user.id" 
                  class="user-item"
                  :class="{ 'user-banned': user.status === 'banned' }"
                >
                  <div class="user-header">
                    <div class="user-basic-info">
                      <div class="user-name">
                        {{ user.name || '名前未設定' }}
                        <span v-if="user.status === 'banned'" class="banned-badge">停止中</span>
                      </div>
                      <div class="user-email">{{ user.email }}</div>
                      <div v-if="user.phone" class="user-phone">📞 {{ user.phone }}</div>
                    </div>
                    <div class="user-status-info">
                      <div class="user-last-login">
                        <div v-if="user.last_login_time" class="last-login-time">
                          最終ログイン: {{ formatDate(user.last_login_time) }}
                        </div>
                        <div v-else class="last-login-time">最終ログイン: 未記録</div>
                        <div v-if="user.last_login_ip" class="last-login-ip">
                          IP: {{ user.last_login_ip }}
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- 用户的课程信息 -->
                  <div class="user-courses">
                    <div class="courses-title">受講中のコース:</div>
                    <div v-if="user.courses && user.courses.length > 0" class="courses-list">
                      <div 
                        v-for="course in user.courses" 
                        :key="course.id" 
                        class="course-tag"
                        :class="'course-' + course.status"
                      >
                        {{ course.course_name }}
                        <span class="course-status">({{ getCourseStatusName(course.status) }})</span>
                        <button 
                          @click="editUserCourse(user, course)" 
                          class="btn-edit-course"
                          title="コース編集"
                        >
                          <i class="fas fa-edit"></i>
                        </button>
                      </div>
                    </div>
                    <div v-else class="no-courses">
                      受講コースなし
                    </div>
                  </div>

                  <!-- 用户操作按钮 -->
                  <div class="user-actions">
                    <button @click="editUser(user)" class="btn btn-sm btn-primary">
                      <i class="fas fa-edit"></i> 編集
                    </button>
                    <button @click="addUserCourse(user)" class="btn btn-sm btn-success">
                      <i class="fas fa-plus"></i> コース追加
                    </button>
                    <button 
                      v-if="user.status === 'active'"
                      @click="banUser(user)" 
                      class="btn btn-sm btn-warning"
                    >
                      <i class="fas fa-ban"></i> 停止
                    </button>
                    <button 
                      v-if="user.status === 'banned'"
                      @click="unbanUser(user)" 
                      class="btn btn-sm btn-success"
                    >
                      <i class="fas fa-check"></i> 復活
                    </button>
                    <button @click="confirmDeleteUser(user)" class="btn btn-sm btn-danger">
                      <i class="fas fa-trash"></i> 削除
                    </button>
                  </div>
                </div>
              </div>

              <!-- 用户编辑模态框 -->
              <div v-if="showEditUserModal" class="modal-overlay">
                <div class="modal-content user-edit-modal">
                  <h4>ユーザー情報編集</h4>
                  <form @submit.prevent="saveUserEdit">
                    <div class="form-group">
                      <label for="edit-user-name">名前</label>
                      <input 
                        type="text" 
                        id="edit-user-name" 
                        v-model="editUserForm.name"
                        placeholder="フルネームを入力"
                      >
                    </div>
                    <div class="form-group">
                      <label for="edit-user-email">メールアドレス</label>
                      <input 
                        type="email" 
                        id="edit-user-email" 
                        v-model="editUserForm.email"
                        required
                      >
                    </div>
                    <div class="form-group">
                      <label for="edit-user-phone">電話番号</label>
                      <input 
                        type="tel" 
                        id="edit-user-phone" 
                        v-model="editUserForm.phone"
                        placeholder="090-1234-5678"
                      >
                    </div>
                    <div class="modal-actions">
                      <button type="button" @click="showEditUserModal = false" class="btn btn-outline">
                        キャンセル
                      </button>
                      <button type="submit" class="btn btn-primary" :disabled="isUpdatingUser">
                        {{ isUpdatingUser ? '更新中...' : '更新' }}
                      </button>
                    </div>
                  </form>
                </div>
              </div>

              <!-- 课程编辑模态框 -->
              <div v-if="showEditCourseModal" class="modal-overlay">
                <div class="modal-content course-edit-modal">
                  <h4>ユーザーコース編集</h4>
                  <form @submit.prevent="saveUserCourseEdit">
                    <div class="form-group">
                      <label for="edit-course-name">コース名</label>
                      <select id="edit-course-name" v-model="editCourseForm.course_name" required @change="onCourseNameChange">
                        <option value="">コースを選択</option>
                        
                        <!-- 子ども向け英語クラス -->
                        <optgroup label="子ども向け英語クラス">
                          <option 
                            v-for="course in availableCourses.filter(c => c.category === 'kids')" 
                            :key="course.id" 
                            :value="course.name"
                            :data-category="course.category"
                            :data-subcategory="course.subcategory || ''"
                          >
                            {{ course.name }} - ¥{{ course.price }}
                          </option>
                        </optgroup>
                        
                        <!-- シニア向け英会話 -->
                        <optgroup label="シニア向け英会話">
                          <option 
                            v-for="course in availableCourses.filter(c => c.category === 'senior')" 
                            :key="course.id" 
                            :value="course.name"
                            :data-category="course.category"
                            :data-subcategory="course.subcategory || ''"
                          >
                            {{ course.name }} - ¥{{ course.price }}
                          </option>
                        </optgroup>
                        
                        <!-- 英検対策クラス -->
                        <optgroup label="英検対策クラス">
                          <option 
                            v-for="course in availableCourses.filter(c => c.category === 'eiken')" 
                            :key="course.id" 
                            :value="course.name"
                            :data-category="course.category"
                            :data-subcategory="course.subcategory || ''"
                          >
                            {{ course.name }}{{ course.subcategory ? ` (${getCourseSubcategoryName(course.subcategory)})` : '' }} - ¥{{ course.price }}
                          </option>
                        </optgroup>
                      </select>
                    </div>
                    <div class="form-group">
                      <label for="edit-course-status">ステータス</label>
                      <select id="edit-course-status" v-model="editCourseForm.status" required>
                        <option value="pending">審査中</option>
                        <option value="approved">承認済み</option>
                        <option value="rejected">拒否</option>
                      </select>
                    </div>
                    <div class="form-group">
                      <label for="edit-course-notes">備考</label>
                      <textarea 
                        id="edit-course-notes" 
                        v-model="editCourseForm.notes"
                        rows="3"
                        placeholder="特記事項があれば記入"
                      ></textarea>
                    </div>
                    <div class="modal-actions">
                      <button type="button" @click="showEditCourseModal = false" class="btn btn-outline">
                        キャンセル
                      </button>
                      <button 
                        v-if="editCourseForm.id"
                        type="button" 
                        @click="deleteUserCourse" 
                        class="btn btn-danger"
                        :disabled="isUpdatingCourse"
                      >
                        削除
                      </button>
                      <button type="submit" class="btn btn-primary" :disabled="isUpdatingCourse">
                        {{ isUpdatingCourse ? '更新中...' : (editCourseForm.id ? '更新' : '追加') }}
                      </button>
                    </div>
                  </form>
                </div>
              </div>

              <!-- 用户删除确认模态框 -->
              <div v-if="showDeleteUserModal" class="modal-overlay">
                <div class="modal-content">
                  <h4>ユーザー削除確認</h4>
                  <p>
                    <strong>{{ userToDelete?.name || userToDelete?.email }}</strong> を削除してもよろしいですか？
                  </p>
                  <p class="warning-text">
                    この操作は元に戻せません。ユーザーの全ての情報とコース履歴が削除されます。
                  </p>
                  <div class="modal-actions">
                    <button @click="showDeleteUserModal = false" class="btn btn-outline">
                      キャンセル
                    </button>
                    <button @click="deleteUser" class="btn btn-danger" :disabled="isDeletingUser">
                      {{ isDeletingUser ? '削除中...' : '削除' }}
                    </button>
                  </div>
                </div>
              </div>

              <!-- 用户封禁模态框 -->
              <div v-if="showBanUserModal" class="modal-overlay">
                <div class="modal-content">
                  <h4>ユーザー停止</h4>
                  <p>
                    <strong>{{ userToBan?.name || userToBan?.email }}</strong> を停止しますか？
                  </p>
                  <div class="form-group">
                    <label for="ban-reason">停止理由</label>
                    <textarea 
                      id="ban-reason" 
                      v-model="banReason"
                      rows="3"
                      placeholder="停止理由を入力してください"
                      required
                    ></textarea>
                  </div>
                  <div class="modal-actions">
                    <button @click="showBanUserModal = false" class="btn btn-outline">
                      キャンセル
                    </button>
                    <button @click="confirmBanUser" class="btn btn-warning" :disabled="isBanningUser">
                      {{ isBanningUser ? '停止中...' : '停止' }}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <!-- Access Info Tab -->
            <div v-show="activeTab === 'access'" class="tab-pane access-management">
              <h3 class="section-subtitle">アクセス設定</h3>
              <div v-if="isLoadingAccess" class="loading-state">アクセス情報を読み込み中...</div>
              <div v-else-if="accessError" class="error-state">
                {{ accessError }}
              </div>
              <form v-else @submit.prevent="saveAccessInfo" class="access-form">
                <div class="form-group">
                  <label for="accessAddress">住所</label>
                  <textarea id="accessAddress" v-model="accessData.address" rows="3" placeholder="〒XXX-XXXX\n東京都渋谷区...\n(改行して入力できます)"></textarea>
                </div>
                <div class="form-group">
                  <label for="accessPhone">電話番号</label>
                  <input type="text" id="accessPhone" v-model="accessData.phoneNumber" placeholder="例: 03-1234-5678">
                </div>
                <div class="form-group">
                  <label for="accessTransport">交通アクセス情報</label>
                  <textarea id="accessTransport" v-model="accessData.transportInfo" rows="4" placeholder="最寄駅：JR山手線 渋谷駅（ハチ公口）より徒歩約10分\n(改行して入力できます)"></textarea>
                </div>
                <div class="form-group">
                  <label for="accessMapUrl">Googleマップ 埋め込みURL</label>
                  <input type="url" id="accessMapUrl" v-model="accessData.googleMapsEmbedUrl" placeholder="Googleマップの共有 > 地図を埋め込む から src='...' のURL部分を貼り付け">
                  <p class="info-text small">Googleマップで場所を検索し、「共有」→「地図を埋め込む」で表示される<code>&lt;iframe src="ここにURL" ...&gt;&lt;/iframe&gt;</code>コード内の<code>src="..."</code>の部分にあるURLのみを貼り付けてください。</p>
                </div>
                <button type="submit" class="btn btn-primary" :disabled="isSavingAccess">
                  {{ isSavingAccess ? '保存中...' : 'アクセス情報を保存' }}
                </button>
                <p v-if="accessSuccess" class="success-message small">{{ accessSuccess }}</p>
              </form>
            </div>
            
            <!-- Messages Management Tab -->
            <div v-show="activeTab === 'messages'" class="tab-pane messages-management">
              <h3 class="section-subtitle">メッセージ一覧</h3>
              
              <div class="messages-filter">
                <div class="filter-item">
                  <label>表示:</label>
                  <el-select v-model="messagesFilter.readStatus" placeholder="全て" style="width: 120px">
                    <el-option label="全て" value="all"></el-option>
                    <el-option label="未読のみ" value="unread"></el-option>
                    <el-option label="既読のみ" value="read"></el-option>
                  </el-select>
                </div>
                <div class="filter-item">
                  <label>ユーザータイプ:</label>
                  <el-select v-model="messagesFilter.userType" placeholder="全て" style="width: 120px">
                    <el-option label="全て" value="all"></el-option>
                    <el-option label="会員" value="member"></el-option>
                    <el-option label="ゲスト" value="guest"></el-option>
                  </el-select>
                </div>
                <button @click="fetchMessages" class="btn btn-sm btn-outline refresh-btn">
                  <i class="fas fa-sync-alt"></i> 更新
                </button>
              </div>
              
              <div v-if="isLoadingMessages" class="loading-state">
                メッセージを読み込み中...
              </div>
              <div v-else-if="messagesError" class="error-state">
                {{ messagesError }}
              </div>
              <div v-else-if="filteredMessages.length === 0" class="empty-state">
                メッセージはありません。
              </div>
              <div v-else class="messages-list">
                <div 
                  v-for="message in filteredMessages" 
                  :key="message.id" 
                  class="message-item"
                  :class="{ 'unread': !message.is_read }"
                >
                  <div class="message-header">
                    <div class="message-header-left">
                      <div class="message-subject">
                        {{ message.subject }}
                        <span v-if="!message.is_read" class="unread-badge">未読</span>
                        <span v-if="message.user_type === 'guest'" class="guest-badge">ゲスト</span>
                        <span v-else class="member-badge">会員</span>
                      </div>
                      <div class="message-from">
                        <span>{{ message.name }}</span>
                        <span class="message-email">{{ message.email }}</span>
                        <span v-if="message.phone" class="message-phone">{{ message.phone }}</span>
                      </div>
                    </div>
                    <div class="message-date">{{ formatMessageDate(message.created_at) }}</div>
                  </div>
                  
                  <div class="message-content">{{ message.message }}</div>
                  
                  <div class="message-footer">
                    <div class="message-ip">IP: {{ message.user_ip || '不明' }}</div>
                    <div class="message-actions">
                      <button 
                        @click="toggleMessageReadStatus(message)" 
                        class="btn btn-sm"
                        :class="message.is_read ? 'btn-outline' : 'btn-primary'"
                      >
                        {{ message.is_read ? '未読にする' : '既読にする' }}
                      </button>
                      <button @click="confirmDeleteMessage(message.id)" class="btn btn-sm btn-danger">
                        削除
                      </button>
                    </div>
                  </div>
                </div>
              </div>
              
              <!-- 削除確認モーダル（メッセージ用） -->
              <div v-if="showDeleteMessageModal" class="delete-modal">
                <div class="delete-modal-content">
                  <h4>削除確認</h4>
                  <p>このメッセージを削除してもよろしいですか？この操作は元に戻せません。</p>
                  <div class="delete-modal-actions">
                    <button @click="showDeleteMessageModal = false" class="btn btn-outline">
                      キャンセル
                    </button>
                    <button @click="deleteMessage" class="btn btn-danger" :disabled="isDeletingMessage">
                      {{ isDeletingMessage ? '削除中...' : '削除' }}
                    </button>
                  </div>
                </div>
              </div>
            </div>
            
            <!-- Course Enrollments Management Tab -->
            <div v-show="activeTab === 'enrollments'" class="tab-pane enrollments-management">
              <h3 class="section-subtitle">コース申し込み管理</h3>
              
              <div class="enrollments-filter">
                <div class="filter-item">
                  <label>ステータス:</label>
                  <el-select v-model="enrollmentsFilter.status" placeholder="全て" style="width: 120px">
                    <el-option label="全て" value="all"></el-option>
                    <el-option label="審査中" value="pending"></el-option>
                    <el-option label="承認済み" value="approved"></el-option>
                    <el-option label="拒否" value="rejected"></el-option>
                  </el-select>
                </div>
                <div class="filter-item">
                  <label>コース:</label>
                  <el-select v-model="enrollmentsFilter.course" placeholder="全て" style="width: 180px">
                    <el-option label="全て" value="all"></el-option>
                    <el-option label="子ども向け英語" value="kids"></el-option>
                    <el-option label="シニア向け英会話" value="senior"></el-option>
                    <el-option label="英検対策" value="eiken"></el-option>
                  </el-select>
                </div>
                <button @click="fetchEnrollments" class="btn btn-sm btn-outline refresh-btn">
                  <i class="fas fa-sync-alt"></i> 更新
                </button>
              </div>
              
              <div v-if="isLoadingEnrollments" class="loading-state">
                申し込み情報を読み込み中...
              </div>
              <div v-else-if="enrollmentsError" class="error-state">
                {{ enrollmentsError }}
              </div>
              <div v-else-if="filteredEnrollments.length === 0" class="empty-state">
                申し込み情報はありません。
              </div>
              <div v-else class="enrollments-list">
                <div 
                  v-for="enrollment in filteredEnrollments" 
                  :key="enrollment.id" 
                  class="enrollment-item"
                  :class="'status-' + enrollment.status"
                >
                  <div class="enrollment-header">
                    <div class="enrollment-header-left">
                      <div class="enrollment-course">
                        {{ enrollment.course_name }}
                        <span class="enrollment-price">¥{{ enrollment.course_price }}</span>
                      </div>
                      <div class="enrollment-user">
                        <span class="user-name">{{ enrollment.user_name }}</span>
                        <span class="user-email">{{ enrollment.user_email }}</span>
                        <span class="user-info">{{ enrollment.user_age }}歳 / {{ enrollment.user_phone }}</span>
                      </div>
                    </div>
                    <div class="enrollment-status-badge" :class="'status-' + enrollment.status">
                      {{ getEnrollmentStatusDisplayName(enrollment.status) }}
                    </div>
                  </div>
                  
                  <div class="enrollment-content">
                    <div v-if="enrollment.message" class="enrollment-message">
                      <strong>受講者からのメッセージ:</strong>
                      <p>{{ enrollment.message }}</p>
                    </div>
                    
                    <div v-if="enrollment.admin_notes" class="admin-notes">
                      <strong>管理者メモ:</strong>
                      <p>{{ enrollment.admin_notes }}</p>
                    </div>
                  </div>
                  
                  <div class="enrollment-footer">
                    <div class="enrollment-date">申し込み日: {{ formatEnrollmentDate(enrollment.created_at) }}</div>
                    <div class="enrollment-actions">
                      <button 
                        @click="editEnrollment(enrollment)" 
                        class="btn btn-sm btn-outline"
                      >
                        編集
                      </button>
                      <button 
                        v-if="enrollment.status === 'pending'"
                        @click="updateEnrollmentStatus(enrollment.id, 'approved')" 
                        class="btn btn-sm btn-success"
                        :disabled="isUpdatingEnrollment"
                      >
                        承認
                      </button>
                      <button 
                        v-if="enrollment.status === 'pending'"
                        @click="updateEnrollmentStatus(enrollment.id, 'rejected')" 
                        class="btn btn-sm btn-danger"
                        :disabled="isUpdatingEnrollment"
                      >
                        拒否
                      </button>
                      <button 
                        @click="confirmDeleteEnrollment(enrollment.id)" 
                        class="btn btn-sm btn-danger"
                      >
                        削除
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <!-- Page Editor Tab -->
            <div v-show="activeTab === 'page-editor'" class="tab-pane page-editor">
              <h3 class="section-subtitle">ページ編集</h3>
              
              <div class="page-selector">
                <label for="page-select">編集するページを選択:</label>
                <select 
                  id="page-select" 
                  v-model="selectedPage" 
                  @change="loadPageContent"
                  class="page-select-dropdown"
                >
                  <option value="">ページを選択してください</option>
                  <option 
                    v-for="(label, key) in availablePages" 
                    :key="key" 
                    :value="key"
                  >
                    {{ label }}
                  </option>
                </select>
              </div>
              
              <div v-if="selectedPage && selectedPage === 'about'" class="page-content-editor">
                <h4>About ページ編集</h4>
                
                <div v-if="isLoadingPageContent" class="loading-state">
                  ページ内容を読み込み中...
                </div>
                
                <div v-else-if="pageContentError" class="error-state">
                  {{ pageContentError }}
                </div>
                
                <form v-else @submit.prevent="savePageContent" class="page-edit-form">
                  <!-- 皆の英語教室へようこそ セクション -->
                  <div class="form-section">
                    <h5>皆の英語教室へようこそ</h5>
                    
                    <div class="form-group">
                      <label for="welcome-title">タイトル</label>
                      <input 
                        type="text" 
                        id="welcome-title" 
                        v-model="pageContent.welcome_title"
                        placeholder="皆の英語教室へようこそ"
                      >
                    </div>
                    
                    <div class="form-group">
                      <label for="welcome-content">内容</label>
                      <textarea 
                        id="welcome-content" 
                        v-model="pageContent.welcome_content"
                        rows="4"
                        placeholder="教室の紹介文を入力してください"
                      ></textarea>
                    </div>
                    
                    <div class="form-group">
                      <label for="welcome-image">メイン画像</label>
                      <input
                        type="file"
                        id="welcome-image"
                        @change="handleWelcomeImageChange"
                        accept="image/*"
                        :disabled="isUploadingPageImage"
                      >
                      <div v-if="pageImageUploadProgress > 0 && pageImageUploadProgress < 100" class="progress">
                        <div class="progress-bar" :style="{ width: pageImageUploadProgress + '%' }"></div>
                        <span>{{ pageImageUploadProgress }}%</span>
                      </div>
                      <div v-if="pageContent.welcome_image" class="image-preview">
                        <img :src="pageContent.welcome_image" alt="プレビュー">
                        <button type="button" @click="removeWelcomeImage" class="btn btn-sm btn-danger">画像を削除</button>
                      </div>
                    </div>
                  </div>
                  
                  <!-- 私たちの使命 セクション -->
                  <div class="form-section">
                    <h5>私たちの使命</h5>
                    
                    <div class="form-group">
                      <label for="mission-title">タイトル</label>
                      <input 
                        type="text" 
                        id="mission-title" 
                        v-model="pageContent.mission_title"
                        placeholder="私たちの使命"
                      >
                    </div>
                    
                    <div class="form-group">
                      <label for="mission-content">内容</label>
                      <textarea 
                        id="mission-content" 
                        v-model="pageContent.mission_content"
                        rows="4"
                        placeholder="使命の内容を入力してください"
                      ></textarea>
                    </div>
                  </div>
                  
                  <!-- 6つのポイント セクション -->
                  <div class="form-section">
                    <h5>皆の英語教室で学ぶ、6つのポイント</h5>
                    
                    <div class="form-group">
                      <label for="features-title">セクションタイトル</label>
                      <input 
                        type="text" 
                        id="features-title" 
                        v-model="pageContent.features_title"
                        placeholder="皆の英語教室で学ぶ、6つのポイント"
                      >
                    </div>
                    
                    <div class="features-editor">
                      <div 
                        v-for="(feature, index) in pageContent.features" 
                        :key="index" 
                        class="feature-item-editor"
                      >
                        <h6>ポイント {{ index + 1 }}</h6>
                        <div class="form-group">
                          <label>アイコン (Font Awesome クラス)</label>
                          <input 
                            type="text" 
                            v-model="feature.icon"
                            placeholder="例: fas fa-graduation-cap"
                          >
                        </div>
                        <div class="form-group">
                          <label>タイトル</label>
                          <input 
                            type="text" 
                            v-model="feature.title"
                            placeholder="ポイントのタイトル"
                          >
                        </div>
                        <div class="form-group">
                          <label>説明</label>
                          <textarea 
                            v-model="feature.description"
                            rows="2"
                            placeholder="ポイントの説明"
                          ></textarea>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <!-- 講師紹介 セクション -->
                  <div class="form-section">
                    <h5>講師紹介</h5>
                    
                    <div class="form-group">
                      <label for="teacher-title">セクションタイトル</label>
                      <input 
                        type="text" 
                        id="teacher-title" 
                        v-model="pageContent.teacher_title"
                        placeholder="講師紹介"
                      >
                    </div>
                    
                    <div class="form-group">
                      <label for="teacher-name">講師名</label>
                      <input 
                        type="text" 
                        id="teacher-name" 
                        v-model="pageContent.teacher_name"
                        placeholder="講師の名前"
                      >
                    </div>
                    
                    <div class="form-group">
                      <label for="teacher-subtitle">講師の肩書き</label>
                      <input 
                        type="text" 
                        id="teacher-subtitle" 
                        v-model="pageContent.teacher_subtitle"
                        placeholder="講師の肩書き"
                      >
                    </div>
                    
                    <div class="form-group">
                      <label for="teacher-description">講師紹介文</label>
                      <textarea 
                        id="teacher-description" 
                        v-model="pageContent.teacher_description"
                        rows="8"
                        placeholder="講師の詳細な紹介文"
                      ></textarea>
                    </div>
                    
                    <div class="form-group">
                      <label for="teacher-image">講師画像</label>
                      <input
                        type="file"
                        id="teacher-image"
                        @change="handleTeacherImageChange"
                        accept="image/*"
                        :disabled="isUploadingPageImage"
                      >
                      <div v-if="pageImageUploadProgress > 0 && pageImageUploadProgress < 100" class="progress">
                        <div class="progress-bar" :style="{ width: pageImageUploadProgress + '%' }"></div>
                        <span>{{ pageImageUploadProgress }}%</span>
                      </div>
                      <div v-if="pageContent.teacher_image" class="image-preview">
                        <img :src="pageContent.teacher_image" alt="講師画像プレビュー">
                        <button type="button" @click="removeTeacherImage" class="btn btn-sm btn-danger">画像を削除</button>
                      </div>
                    </div>
                  </div>
                  
                  <!-- 教室の様子 セクション -->
                  <div class="form-section">
                    <h5>教室の様子</h5>
                    
                    <div class="form-group">
                      <label for="gallery-title">セクションタイトル</label>
                      <input 
                        type="text" 
                        id="gallery-title" 
                        v-model="pageContent.gallery_title"
                        placeholder="教室の様子"
                      >
                    </div>
                    
                    <div class="gallery-editor">
                      <div 
                        v-for="(image, index) in pageContent.gallery_images" 
                        :key="index" 
                        class="gallery-item-editor"
                      >
                        <h6>画像 {{ index + 1 }}</h6>
                        <div class="form-group">
                          <label>画像ファイル</label>
                          <input
                            type="file"
                            @change="(e) => handleGalleryImageChange(e, index)"
                            accept="image/*"
                            :disabled="isUploadingPageImage"
                          >
                          <div v-if="image" class="image-preview">
                            <img :src="image" :alt="`教室の様子${index + 1}`">
                            <button type="button" @click="removeGalleryImage(index)" class="btn btn-sm btn-danger">画像を削除</button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <!-- CTAセクション -->
                  <div class="form-section">
                    <h5>お問い合わせセクション</h5>
                    
                    <div class="form-group">
                      <label for="courses-cta-title">CTAタイトル</label>
                      <input 
                        type="text" 
                        id="courses-cta-title" 
                        v-model="pageContent.cta_title"
                        placeholder="体験レッスン受付中！"
                      >
                    </div>
                    
                    <div class="form-group">
                      <label for="courses-cta-text">CTA説明文</label>
                      <textarea 
                        id="courses-cta-text" 
                        v-model="pageContent.cta_text"
                        rows="2"
                        placeholder="どのコースも初回は無料で体験できます。お気軽にお問い合わせください。"
                      ></textarea>
                    </div>
                  </div>
                  
                  <div class="form-actions">
                    <button type="submit" class="btn btn-primary" :disabled="isSavingPageContent || isUploadingPageImage">
                      {{ isSavingPageContent ? '保存中...' : (isUploadingPageImage ? '画像処理中...' : 'ページ内容を保存') }}
                    </button>
                  </div>
                  
                  <p v-if="pageContentSuccess" class="success-message">{{ pageContentSuccess }}</p>
                </form>
              </div>
              
              <!-- Courses Page Editor -->
              <div v-else-if="selectedPage === 'courses'" class="page-content-editor">
                <h4>Courses ページ編集</h4>
                
                <div v-if="isLoadingPageContent" class="loading-state">
                  ページ内容を読み込み中...
                </div>
                
                <div v-else-if="pageContentError" class="error-state">
                  {{ pageContentError }}
                </div>
                
                <form v-else @submit.prevent="savePageContent" class="page-edit-form">
                  <!-- 基本情報セクション -->
                  <div class="form-section">
                    <h5>基本情報</h5>
                    
                    <div class="form-group">
                      <label for="courses-page-title">ページタイトル</label>
                      <input 
                        type="text" 
                        id="courses-page-title" 
                        v-model="pageContent.page_title"
                        placeholder="コース紹介"
                      >
                    </div>
                    
                    <div class="form-group">
                      <label for="courses-intro-text">紹介文</label>
                      <textarea 
                        id="courses-intro-text" 
                        v-model="pageContent.intro_text"
                        rows="3"
                        placeholder="皆の英語教室では、年齢や目的に合わせたさまざまなコースをご用意しています。"
                      ></textarea>
                    </div>
                  </div>
                  
                  <!-- コース管理セクション -->
                  <div class="form-section">
                    <h5>コース管理</h5>
                    
                    <div class="courses-editor">
                      <div 
                        v-for="(course, index) in coursesData" 
                        :key="index" 
                        class="course-item-editor"
                      >
                        <div class="course-editor-header">
                          <h6>コース {{ index + 1 }}</h6>
                          <button 
                            type="button" 
                            @click="removeCourse(index)" 
                            class="btn btn-sm btn-danger"
                            :disabled="coursesData.length <= 1"
                          >
                            削除
                          </button>
                        </div>
                        
                        <div class="form-group">
                          <label>コースタイトル</label>
                          <input 
                            type="text" 
                            v-model="course.title"
                            placeholder="コースのタイトル"
                          >
                        </div>
                        
                        <div class="form-group">
                          <label>スケジュール</label>
                          <input 
                            type="text" 
                            v-model="course.schedule"
                            placeholder="例: 毎週木曜日 16:00 - 17:00"
                          >
                        </div>
                        
                        <div class="form-group">
                          <label>コース画像</label>
                          <input
                            type="file"
                            @change="(e) => handleCourseImageChange(e, index)"
                            accept="image/*"
                            :disabled="isUploadingPageImage"
                          >
                          <div v-if="pageImageUploadProgress > 0 && pageImageUploadProgress < 100" class="progress">
                            <div class="progress-bar" :style="{ width: pageImageUploadProgress + '%' }"></div>
                            <span>{{ pageImageUploadProgress }}%</span>
                          </div>
                          <div v-if="course.image" class="image-preview">
                            <img :src="course.image" alt="コース画像プレビュー" style="max-width: 200px;">
                            <button type="button" @click="removeCourseImage(index)" class="btn btn-sm btn-danger" :disabled="isUploadingPageImage">画像を削除</button>
                          </div>
                          <div v-if="!course.image" class="no-image-placeholder">
                            <img src="@/assets/course/kids.png" alt="デフォルト画像" style="max-width: 200px; opacity: 0.5;">
                            <p style="color: #666; font-size: 14px;">画像をアップロードしてください</p>
                          </div>
                        </div>
                        
                        <div class="form-group">
                          <label>コース説明</label>
                          <textarea 
                            v-model="course.description"
                            rows="4"
                            placeholder="コースの詳細説明（改行は\nで表現）"
                          ></textarea>
                        </div>
                        
                        <div class="form-group">
                          <label>おすすめ対象者（1行に1つ）</label>
                          <textarea 
                            v-model="course.target_audience_text"
                            rows="3"
                            placeholder="例：\n初めて英語を学ぶお子様\n英語に興味のあるお子様"
                            @input="updateTargetAudience(course, $event)"
                          ></textarea>
                        </div>
                        
                        <div class="form-group">
                          <label>料金情報</label>
                          <textarea 
                            v-model="course.price"
                            rows="3"
                            placeholder="料金の詳細（改行は\nで表現）"
                          ></textarea>
                        </div>
                      </div>
                      
                      <button 
                        type="button" 
                        @click="addCourse" 
                        class="btn btn-success add-course-btn"
                      >
                        <i class="fas fa-plus"></i> 新しいコースを追加
                      </button>
                    </div>
                  </div>
                  
                  <!-- CTAセクション -->
                  <div class="form-section">
                    <h5>お問い合わせセクション</h5>
                    
                    <div class="form-group">
                      <label for="courses-cta-title">CTAタイトル</label>
                      <input 
                        type="text" 
                        id="courses-cta-title" 
                        v-model="pageContent.cta_title"
                        placeholder="体験レッスン受付中！"
                      >
                    </div>
                    
                    <div class="form-group">
                      <label for="courses-cta-text">CTA説明文</label>
                      <textarea 
                        id="courses-cta-text" 
                        v-model="pageContent.cta_text"
                        rows="2"
                        placeholder="どのコースも初回は無料で体験できます。お気軽にお問い合わせください。"
                      ></textarea>
                    </div>
                  </div>
                  
                  <div class="form-actions">
                    <button type="submit" class="btn btn-primary" :disabled="isSavingPageContent">
                      {{ isSavingPageContent ? '保存中...' : 'ページ内容を保存' }}
                    </button>
                  </div>
                  
                  <p v-if="pageContentSuccess" class="success-message">{{ pageContentSuccess }}</p>
                </form>
              </div>
              
              <!-- Home Page Editor -->
              <div v-else-if="selectedPage === 'home'" class="page-content-editor">
                <h4>Home ページ編集</h4>
                
                <div v-if="isLoadingPageContent" class="loading-state">
                  ページ内容を読み込み中...
                </div>
                
                <div v-else-if="pageContentError" class="error-state">
                  {{ pageContentError }}
                </div>
                
                <form v-else @submit.prevent="savePageContent" class="page-edit-form">
                  <!-- Hero横幅セクション -->
                  <div class="form-section">
                    <h5>Hero横幅セクション</h5>
                    
                    <div class="form-group">
                      <label for="home-hero-title">メインタイトル</label>
                      <input 
                        type="text" 
                        id="home-hero-title" 
                        v-model="pageContent.home_hero_title"
                        placeholder="皆の英語教室"
                      >
                    </div>
                    
                    <div class="form-group">
                      <label for="home-hero-subtitle">サブタイトル</label>
                      <input 
                        type="text" 
                        id="home-hero-subtitle" 
                        v-model="pageContent.home_hero_subtitle"
                        placeholder="楽しく、心を動かす英語教室！"
                      >
                    </div>
                    
                    <div class="form-group">
                      <label for="home-hero-text">説明文</label>
                      <textarea 
                        id="home-hero-text" 
                        v-model="pageContent.home_hero_text"
                        rows="2"
                        placeholder="自信を持って英語を学ぶ。実践的なレッスンで英語力アップ！"
                      ></textarea>
                    </div>
                    
                    <div class="form-group">
                      <label for="home-hero-button1">ボタン1のテキスト</label>
                      <input 
                        type="text" 
                        id="home-hero-button1" 
                        v-model="pageContent.home_hero_button1"
                        placeholder="コースを見る"
                      >
                    </div>
                    
                    <div class="form-group">
                      <label for="home-hero-button2">ボタン2のテキスト</label>
                      <input 
                        type="text" 
                        id="home-hero-button2" 
                        v-model="pageContent.home_hero_button2"
                        placeholder="お問い合わせ"
                      >
                    </div>
                    
                    <div class="form-group">
                      <label for="home-hero-image">Hero画像</label>
                      <input
                        type="file"
                        id="home-hero-image"
                        @change="handleHeroImageChange"
                        accept="image/*"
                        :disabled="isUploadingPageImage"
                      >
                      <div v-if="pageImageUploadProgress > 0 && pageImageUploadProgress < 100" class="progress">
                        <div class="progress-bar" :style="{ width: pageImageUploadProgress + '%' }"></div>
                        <span>{{ pageImageUploadProgress }}%</span>
                      </div>
                      <div v-if="pageContent.home_hero_image" class="image-preview">
                        <img :src="pageContent.home_hero_image" alt="Hero画像プレビュー" style="max-width: 300px;">
                        <button type="button" @click="removeHeroImage" class="btn btn-sm btn-danger">画像を削除</button>
                      </div>
                      <div v-if="!pageContent.home_hero_image" class="no-image-placeholder">
                        <img src="@/assets/banner.png" alt="デフォルト画像" style="max-width: 300px; opacity: 0.5;">
                        <p style="color: #666; font-size: 14px;">画像をアップロードしてください（デフォルト: banner.png）</p>
                      </div>
                    </div>
                  </div>
                  
                  <!-- 6つのポイント セクション -->
                  <div class="form-section">
                    <h5>6つのポイント</h5>
                    
                    <div class="form-group">
                      <label for="home-features-title">セクションタイトル</label>
                      <input 
                        type="text" 
                        id="home-features-title" 
                        v-model="pageContent.home_features_title"
                        placeholder="皆の英語教室で学ぶ、6つのポイント"
                      >
                    </div>
                    
                    <div class="features-editor">
                      <div 
                        v-for="(feature, index) in pageContent.home_features" 
                        :key="index" 
                        class="feature-item-editor"
                      >
                        <h6>ポイント {{ index + 1 }}</h6>
                        <div class="form-group">
                          <label>アイコン (Font Awesome クラス)</label>
                          <input 
                            type="text" 
                            v-model="feature.icon"
                            placeholder="例: fas fa-graduation-cap"
                          >
                        </div>
                        <div class="form-group">
                          <label>タイトル</label>
                          <input 
                            type="text" 
                            v-model="feature.title"
                            placeholder="ポイントのタイトル"
                          >
                        </div>
                        <div class="form-group">
                          <label>説明</label>
                          <textarea 
                            v-model="feature.description"
                            rows="2"
                            placeholder="ポイントの説明"
                          ></textarea>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <!-- コースのご案内 セクション -->
                  <div class="form-section">
                    <h5>コースのご案内</h5>
                    
                    <div class="form-group">
                      <label for="home-courses-title">セクションタイトル</label>
                      <input 
                        type="text" 
                        id="home-courses-title" 
                        v-model="pageContent.home_courses_title"
                        placeholder="コースのご案内"
                      >
                    </div>
                    
                    <div class="form-group">
                      <label for="home-courses-button">「もっと詳しく」ボタンテキスト</label>
                      <input 
                        type="text" 
                        id="home-courses-button" 
                        v-model="pageContent.home_courses_button"
                        placeholder="もっと詳しく"
                      >
                    </div>
                    
                    <div class="courses-editor">
                      <div 
                        v-for="(course, index) in pageContent.home_courses" 
                        :key="index" 
                        class="course-item-editor"
                      >
                        <div class="course-editor-header">
                          <h6>コース {{ index + 1 }}</h6>
                          <button 
                            type="button" 
                            @click="removeHomeCourse(index)" 
                            class="btn btn-sm btn-danger"
                            :disabled="pageContent.home_courses.length <= 1"
                          >
                            削除
                          </button>
                        </div>
                        
                        <div class="form-group">
                          <label>コースタイトル</label>
                          <input 
                            type="text" 
                            v-model="course.title"
                            placeholder="コースのタイトル"
                          >
                        </div>
                        
                        <div class="form-group">
                          <label>スケジュール</label>
                          <input 
                            type="text" 
                            v-model="course.schedule"
                            placeholder="例: 毎週木曜日 16:00 - 17:00"
                          >
                        </div>
                        
                        <div class="form-group">
                          <label>コース説明</label>
                          <textarea 
                            v-model="course.description"
                            rows="2"
                            placeholder="コースの簡単な説明"
                          ></textarea>
                        </div>
                        
                        <div class="form-group">
                          <label>カラークラス</label>
                          <select v-model="course.colorClass">
                            <option value="pink">ピンク</option>
                            <option value="green">グリーン</option>
                            <option value="yellow">イエロー</option>
                            <option value="blue">ブルー</option>
                            <option value="purple">パープル</option>
                          </select>
                        </div>
                        
                        <div class="form-group">
                          <label>コース画像ファイル名</label>
                          <input 
                            type="text" 
                            v-model="course.image"
                            placeholder="例: kids.png"
                          >
                          <small style="color: #666;">assets/course/ フォルダ内の画像ファイル名を入力してください</small>
                        </div>
                      </div>
                      
                      <button 
                        type="button" 
                        @click="addHomeCourse" 
                        class="btn btn-success add-course-btn"
                      >
                        <i class="fas fa-plus"></i> 新しいコースを追加
                      </button>
                    </div>
                  </div>
                  
                  <!-- 生徒様の声 セクション -->
                  <div class="form-section">
                    <h5>生徒様の声</h5>
                    
                    <div class="testimonials-editor">
                      <div 
                        v-for="(testimonial, index) in pageContent.home_testimonials" 
                        :key="index" 
                        class="testimonial-item-editor"
                      >
                        <div class="testimonial-editor-header">
                          <h6>生徒様の声 {{ index + 1 }}</h6>
                          <button 
                            type="button" 
                            @click="removeTestimonial(index)" 
                            class="btn btn-sm btn-danger"
                            :disabled="pageContent.home_testimonials.length <= 1"
                          >
                            削除
                          </button>
                        </div>
                        
                        <div class="form-group">
                          <label>お名前</label>
                          <input 
                            type="text" 
                            v-model="testimonial.name"
                            placeholder="例: 清水さん（保護者様）"
                          >
                        </div>
                        
                        <div class="form-group">
                          <label>受講コース</label>
                          <input 
                            type="text" 
                            v-model="testimonial.course"
                            placeholder="例: 子供向けクラス受講"
                          >
                        </div>
                        
                        <div class="form-group">
                          <label>コメント</label>
                          <textarea 
                            v-model="testimonial.content"
                            rows="3"
                            placeholder="生徒様のコメント"
                          ></textarea>
                        </div>
                        
                        <div class="form-group">
                          <label>画像ファイル</label>
                          <input
                            type="file"
                            @change="(e) => handleTestimonialImageChange(e, index)"
                            accept="image/*"
                            :disabled="isUploadingPageImage"
                          >
                          <div v-if="pageImageUploadProgress > 0 && pageImageUploadProgress < 100" class="progress">
                            <div class="progress-bar" :style="{ width: pageImageUploadProgress + '%' }"></div>
                            <span>{{ pageImageUploadProgress }}%</span>
                          </div>
                          <div v-if="testimonial.image" class="image-preview">
                            <img :src="getTestimonialImageUrl(testimonial.image)" alt="生徒様の画像" style="max-width: 120px; max-height: 120px; border-radius: 50%;">
                            <button type="button" @click="removeTestimonialImage(index)" class="btn btn-sm btn-danger">画像を削除</button>
                          </div>
                          <div v-if="!testimonial.image || isDefaultImage(testimonial.image)" class="no-image-placeholder">
                            <img :src="getTestimonialImageUrl(testimonial.image || 'q1.jpg')" alt="デフォルト画像" style="max-width: 120px; max-height: 120px; border-radius: 50%; opacity: 0.5;">
                            <p style="color: #666; font-size: 14px;">画像をアップロードしてください</p>
                          </div>
                        </div>
                      </div>
                      
                      <button 
                        type="button" 
                        @click="addTestimonial" 
                        class="btn btn-success add-testimonial-btn"
                      >
                        <i class="fas fa-plus"></i> 新しい生徒様の声を追加
                      </button>
                    </div>
                  </div>
                  
                  <!-- CTA（お問い合わせ）セクション -->
                  <div class="form-section">
                    <h5>CTA（お問い合わせ）セクション</h5>
                    
                    <div class="form-group">
                      <label for="home-cta-title">CTAタイトル</label>
                      <input 
                        type="text" 
                        id="home-cta-title" 
                        v-model="pageContent.home_cta_title"
                        placeholder="まずはお気軽に体験レッスンから！"
                      >
                    </div>
                    
                    <div class="form-group">
                      <label for="home-cta-text">CTA説明文</label>
                      <input 
                        type="text" 
                        id="home-cta-text" 
                        v-model="pageContent.home_cta_text"
                        placeholder="4レッスン 4950円〜"
                      >
                    </div>
                    
                    <div class="form-group">
                      <label for="home-cta-button">CTAボタンテキスト</label>
                      <input 
                        type="text" 
                        id="home-cta-button" 
                        v-model="pageContent.home_cta_button"
                        placeholder="お問い合わせ"
                      >
                    </div>
                  </div>
                  
                  <div class="form-actions">
                    <button type="submit" class="btn btn-primary" :disabled="isSavingPageContent || isUploadingPageImage">
                      {{ isSavingPageContent ? '保存中...' : (isUploadingPageImage ? '画像処理中...' : 'ページ内容を保存') }}
                    </button>
                  </div>
                  
                  <p v-if="pageContentSuccess" class="success-message">{{ pageContentSuccess }}</p>
                </form>
              </div>
              
              <!-- Header Page Editor -->
              <div v-else-if="selectedPage === 'header'" class="page-content-editor">
                <h4>Header ページ編集</h4>
                
                <div v-if="isLoadingPageContent" class="loading-state">
                  ページ内容を読み込み中...
                </div>
                
                <div v-else-if="pageContentError" class="error-state">
                  {{ pageContentError }}
                </div>
                
                <form v-else @submit.prevent="savePageContent" class="page-edit-form">
                  <!-- ロゴセクション -->
                  <div class="form-section">
                    <h5>ロゴ設定</h5>
                    
                    <div class="form-group">
                      <label for="header-logo">ロゴ画像</label>
                      <input
                        type="file"
                        id="header-logo"
                        @change="handleHeaderLogoChange"
                        accept="image/*"
                        :disabled="isUploadingPageImage"
                      >
                      <div v-if="pageImageUploadProgress > 0 && pageImageUploadProgress < 100" class="progress">
                        <div class="progress-bar" :style="{ width: pageImageUploadProgress + '%' }"></div>
                        <span>{{ pageImageUploadProgress }}%</span>
                      </div>
                      <div v-if="pageContent.header_logo" class="image-preview">
                        <img :src="pageContent.header_logo" alt="ロゴプレビュー" style="max-width: 200px; max-height: 100px;">
                        <button type="button" @click="removeHeaderLogo" class="btn btn-sm btn-danger">ロゴを削除</button>
                      </div>
                      <div v-if="!pageContent.header_logo" class="no-image-placeholder">
                        <img src="@/assets/images/logo_transparent.png" alt="デフォルトロゴ" style="max-width: 200px; max-height: 100px; opacity: 0.5;">
                        <p style="color: #666; font-size: 14px;">ロゴ画像をアップロードしてください（デフォルト: logo_transparent.png）</p>
                      </div>
                    </div>
                    
                    <div class="form-group">
                      <label for="header-title">サイトタイトル</label>
                      <input 
                        type="text" 
                        id="header-title" 
                        v-model="pageContent.header_title"
                        placeholder="皆の英語教室"
                      >
                    </div>
                  </div>
                  
                  <!-- ナビゲーションメニュー設定 -->
                  <div class="form-section">
                    <h5>ナビゲーションメニュー</h5>
                    
                    <div class="form-group">
                      <label for="header-nav-home">ホームメニュー</label>
                      <input 
                        type="text" 
                        id="header-nav-home" 
                        v-model="pageContent.header_nav_home"
                        placeholder="ホーム"
                      >
                    </div>
                    
                    <div class="form-group">
                      <label for="header-nav-about">教室紹介メニュー</label>
                      <input 
                        type="text" 
                        id="header-nav-about" 
                        v-model="pageContent.header_nav_about"
                        placeholder="教室紹介"
                      >
                    </div>
                    
                    <div class="form-group">
                      <label for="header-nav-courses">コース・料金メニュー</label>
                      <input 
                        type="text" 
                        id="header-nav-courses" 
                        v-model="pageContent.header_nav_courses"
                        placeholder="コース・料金"
                      >
                    </div>
                    
                    <div class="form-group">
                      <label for="header-nav-notices">お知らせメニュー</label>
                      <input 
                        type="text" 
                        id="header-nav-notices" 
                        v-model="pageContent.header_nav_notices"
                        placeholder="お知らせ"
                      >
                    </div>
                    
                    <div class="form-group">
                      <label for="header-nav-access">アクセスメニュー</label>
                      <input 
                        type="text" 
                        id="header-nav-access" 
                        v-model="pageContent.header_nav_access"
                        placeholder="アクセス"
                      >
                    </div>
                    
                    <div class="form-group">
                      <label for="header-nav-contact">お問い合わせメニュー</label>
                      <input 
                        type="text" 
                        id="header-nav-contact" 
                        v-model="pageContent.header_nav_contact"
                        placeholder="お問い合わせ"
                      >
                    </div>
                  </div>
                  
                  <div class="form-actions">
                    <button type="submit" class="btn btn-primary" :disabled="isSavingPageContent || isUploadingPageImage">
                      {{ isSavingPageContent ? '保存中...' : (isUploadingPageImage ? '画像処理中...' : 'ヘッダー設定を保存') }}
                    </button>
                  </div>
                  
                  <p v-if="pageContentSuccess" class="success-message">{{ pageContentSuccess }}</p>
                </form>
              </div>
              
              <!-- Footer Page Editor -->
              <div v-else-if="selectedPage === 'footer'" class="page-content-editor">
                <h4>Footer ページ編集</h4>
                
                <div v-if="isLoadingPageContent" class="loading-state">
                  ページ内容を読み込み中...
                </div>
                
                <div v-else-if="pageContentError" class="error-state">
                  {{ pageContentError }}
                </div>
                
                <form v-else @submit.prevent="savePageContent" class="page-edit-form">
                  <!-- ロゴセクション -->
                  <div class="form-section">
                    <h5>フッターロゴ設定</h5>
                    
                    <div class="form-group">
                      <label for="footer-logo-title">ロゴタイトル</label>
                      <input 
                        type="text" 
                        id="footer-logo-title" 
                        v-model="pageContent.footer_logo_title"
                        placeholder="皆の英語教室"
                      >
                    </div>
                    
                    <div class="form-group">
                      <label for="footer-logo-subtitle">ロゴサブタイトル</label>
                      <input 
                        type="text" 
                        id="footer-logo-subtitle" 
                        v-model="pageContent.footer_logo_subtitle"
                        placeholder="自信を持って英語を学ぶ。"
                      >
                    </div>
                  </div>
                  
                  <!-- お問い合わせ情報 -->
                  <div class="form-section">
                    <h5>お問い合わせ情報</h5>
                    
                    <div class="form-group">
                      <label for="footer-contact-title">お問い合わせタイトル</label>
                      <input 
                        type="text" 
                        id="footer-contact-title" 
                        v-model="pageContent.footer_contact_title"
                        placeholder="お問い合わせ"
                      >
                    </div>
                    
                    <div class="form-group">
                      <label for="footer-contact-address">住所</label>
                      <textarea 
                        id="footer-contact-address" 
                        v-model="pageContent.footer_contact_address"
                        rows="4"
                        placeholder="📍 中崎町ホール&#10;〒530-0015&#10;大阪府大阪市北区中崎西1丁目6-8"
                      ></textarea>
                    </div>
                    
                    <div class="form-group">
                      <label for="footer-contact-email">メールアドレス</label>
                      <input 
                        type="text" 
                        id="footer-contact-email" 
                        v-model="pageContent.footer_contact_email"
                        placeholder="✉️ ateloetti@gmail.com"
                      >
                    </div>
                  </div>
                  
                  <!-- クイックリンク -->
                  <div class="form-section">
                    <h5>クイックリンク</h5>
                    
                    <div class="form-group">
                      <label for="footer-links-title">リンクセクションタイトル</label>
                      <input 
                        type="text" 
                        id="footer-links-title" 
                        v-model="pageContent.footer_links_title"
                        placeholder="クイックリンク"
                      >
                    </div>
                    
                    <div class="links-editor">
                      <div 
                        v-for="(link, index) in pageContent.footer_links" 
                        :key="index" 
                        class="link-item-editor"
                      >
                        <div class="link-editor-header">
                          <h6>リンク {{ index + 1 }}</h6>
                          <button 
                            type="button" 
                            @click="removeFooterLink(index)" 
                            class="btn btn-sm btn-danger"
                            :disabled="pageContent.footer_links.length <= 1"
                          >
                            削除
                          </button>
                        </div>
                        
                        <div class="form-group">
                          <label>リンク名</label>
                          <input 
                            type="text" 
                            v-model="link.name"
                            placeholder="例: ホーム"
                          >
                        </div>
                        
                        <div class="form-group">
                          <label>URL</label>
                          <input 
                            type="text" 
                            v-model="link.url"
                            placeholder="例: /"
                          >
                        </div>
                      </div>
                      
                      <button 
                        type="button" 
                        @click="addFooterLink" 
                        class="btn btn-success add-link-btn"
                      >
                        <i class="fas fa-plus"></i> 新しいリンクを追加
                      </button>
                    </div>
                  </div>
                  
                  <!-- 固定版権情報の説明 -->
                  <div class="form-section">
                    <h5>版権情報（編集不可）</h5>
                    <div class="copyright-info">
                      <p style="color: #666; font-style: italic;">
                        版権情報は固定されており、編集できません：<br>
                        © {{ new Date().getFullYear() }} ateloacademy. All Rights Reserved.<br>
                        Powered by <a href="https://oo-sawa.co.jp/" target="_blank" style="color: var(--primary-color);">大澤商事株式会社</a>
                      </p>
                    </div>
                  </div>
                  
                  <div class="form-actions">
                    <button type="submit" class="btn btn-primary" :disabled="isSavingPageContent">
                      {{ isSavingPageContent ? '保存中...' : 'フッター設定を保存' }}
                    </button>
                  </div>
                  
                  <p v-if="pageContentSuccess" class="success-message">{{ pageContentSuccess }}</p>
                </form>
              </div>
              
              <div v-else-if="selectedPage && selectedPage !== 'about' && selectedPage !== 'courses' && selectedPage !== 'home' && selectedPage !== 'header' && selectedPage !== 'footer'" class="coming-soon">
                <p>{{ availablePages[selectedPage] }} の編集機能は準備中です。</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
    
    <!-- 申し込み編集モーダル -->
    <div v-if="showEditEnrollmentModal" class="delete-modal">
      <div class="delete-modal-content" style="max-width: 600px;">
        <h4>申し込み情報編集</h4>
        <form @submit.prevent="saveEnrollmentEdit">
          <div class="form-group">
            <label for="edit-enrollment-status">ステータス</label>
            <select 
              id="edit-enrollment-status" 
              v-model="editEnrollmentForm.status"
              class="form-select"
            >
              <option value="pending">審査中</option>
              <option value="approved">承認済み</option>
              <option value="rejected">拒否</option>
            </select>
          </div>
          
          <div class="form-group">
            <label for="edit-enrollment-notes">管理者メモ</label>
            <textarea 
              id="edit-enrollment-notes" 
              v-model="editEnrollmentForm.admin_notes"
              rows="4"
              placeholder="管理者からのメッセージや備考を入力"
              class="form-textarea"
            ></textarea>
          </div>
          
          <div class="enrollment-info-display">
            <h5>申し込み情報</h5>
            <p><strong>コース:</strong> {{ editEnrollmentForm.course_name }}</p>
            <p><strong>料金:</strong> ¥{{ editEnrollmentForm.course_price }}</p>
            <p><strong>申込者:</strong> {{ editEnrollmentForm.user_name }} ({{ editEnrollmentForm.user_age }}歳)</p>
            <p><strong>連絡先:</strong> {{ editEnrollmentForm.user_email }} / {{ editEnrollmentForm.user_phone }}</p>
            <p v-if="editEnrollmentForm.message"><strong>メッセージ:</strong> {{ editEnrollmentForm.message }}</p>
          </div>
          
          <div class="delete-modal-actions">
            <button type="button" @click="showEditEnrollmentModal = false" class="btn btn-outline">
              キャンセル
            </button>
            <button type="submit" class="btn btn-primary" :disabled="isUpdatingEnrollment">
              {{ isUpdatingEnrollment ? '更新中...' : '更新' }}
            </button>
          </div>
        </form>
      </div>
    </div>
    
    <!-- 申し込み削除確認モーダル -->
    <div v-if="showDeleteEnrollmentModal" class="delete-modal">
      <div class="delete-modal-content">
        <h4>削除確認</h4>
        <p>この申し込み情報を削除してもよろしいですか？この操作は元に戻せません。</p>
        <div class="delete-modal-actions">
          <button @click="showDeleteEnrollmentModal = false" class="btn btn-outline">
            キャンセル
          </button>
          <button @click="deleteEnrollment" class="btn btn-danger" :disabled="isDeletingEnrollment">
            {{ isDeletingEnrollment ? '削除中...' : '削除' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, reactive, onMounted, computed } from 'vue';
import { 
  getAllEnrollments, 
  updateEnrollmentStatus as updateEnrollmentStatusAPI, 
  deleteEnrollment as deleteEnrollmentAPI,
  getStatusDisplayName 
} from '@/api/courseEnrollment';

export default {
  name: 'AdminPage',
  setup() {
    // 認証状態
    const user = ref(null);
    const loginForm = reactive({
      email: '',
      password: ''
    });
    const isLoading = ref(false);
    const loginError = ref('');
    
    // タブ管理
    const activeTab = ref('notices');
    
    // お知らせデータ
    const notices = ref([]);
    const isLoadingNotices = ref(false);
    const noticeForm = reactive({
      title: '',
      date: new Date().toISOString().substr(0, 10),
      content: '',
      isImportant: false,
      imageUrl: ''
    });
    const showAddNoticeForm = ref(false);
    const isSubmitting = ref(false);
    const uploadProgress = ref(0);
    const isUploadingImage = ref(false);
    
    // 編集用
    const editingNotice = ref(null);
    const editNoticeForm = reactive({
      id: '',
      title: '',
      date: '',
      content: '',
      isImportant: false,
      imageUrl: ''
    });
    
    // 削除用
    const showDeleteModal = ref(false);
    const noticeToDeleteId = ref(null);
    const isDeleting = ref(false);
    
    // ユーザー情報
    const users = ref([]);
    const isLoadingUsers = ref(false);
    const usersError = ref('');
    
    // Access Info State
    const accessData = ref({
      address: '',
      phoneNumber: '',
      transportInfo: '',
      googleMapsEmbedUrl: ''
    });
    const isLoadingAccess = ref(false);
    const isSavingAccess = ref(false);
    const accessError = ref('');
    const accessSuccess = ref('');
    
    // Messages State
    const messages = ref([]);
    const isLoadingMessages = ref(false);
    const messagesError = ref('');
    const messagesFilter = reactive({
      readStatus: 'all',
      userType: 'all'
    });
    const showDeleteMessageModal = ref(false);
    const messageToDeleteId = ref(null);
    const isDeletingMessage = ref(false);
    
    // Page Editor State
    const selectedPage = ref('');
    const availablePages = ref({
      about: 'About (教室について)',
      courses: 'Courses (コース案内)',
      home: 'Home (ホーム)',
      header: 'Header (ナビゲーション)',
      footer: 'Footer (フッター)'
    });
    const pageContent = ref({
      welcome_title: '',
      welcome_content: '',
      welcome_image: '',
      mission_title: '',
      mission_content: '',
      features_title: '',
      features: [],
      teacher_title: '',
      teacher_name: '',
      teacher_subtitle: '',
      teacher_description: '',
      teacher_image: '',
      gallery_title: '',
      gallery_images: [],
      // Courses page content
      page_title: '',
      intro_text: '',
      cta_title: '',
      cta_text: '',
      courses_data: '',
      // Home page content
      home_features_title: '',
      home_features: [],
      home_testimonials: [],
      home_hero_title: '',
      home_hero_subtitle: '',
      home_hero_text: '',
      home_hero_button1: '',
      home_hero_button2: '',
      home_hero_image: '',
      home_courses_title: '',
      home_courses_button: '',
      home_courses: [],
      home_cta_title: '',
      home_cta_text: '',
      home_cta_button: '',
      // Header content
      header_logo: '',
      header_title: '',
      header_nav_home: '',
      header_nav_about: '',
      header_nav_courses: '',
      header_nav_notices: '',
      header_nav_access: '',
      header_nav_contact: '',
      // Footer content
      footer_logo_title: '',
      footer_logo_subtitle: '',
      footer_contact_title: '',
      footer_contact_address: '',
      footer_contact_email: '',
      footer_links_title: '',
      footer_links: []
    });
    const isLoadingPageContent = ref(false);
    const pageContentError = ref('');
    const pageImageUploadProgress = ref(0);
    const isUploadingPageImage = ref(false);
    const pageContentSuccess = ref('');
    const isSavingPageContent = ref(false);
    
    // Courses data management
    const coursesData = ref([]);
    const defaultCourseData = () => ({
      id: Date.now(),
      title: '',
      image: '', // 默认为空，显示占位符
      schedule: '',
      description: '',
      target_audience: [],
      target_audience_text: '',
      price: ''
    });
    
    // Course Enrollments State
    const enrollments = ref([]);
    const isLoadingEnrollments = ref(false);
    const enrollmentsError = ref('');
    const enrollmentsFilter = reactive({
      status: 'all',
      course: 'all'
    });
    
    // Enrollment edit/delete state
    const showEditEnrollmentModal = ref(false);
    const showDeleteEnrollmentModal = ref(false);
    const editingEnrollment = ref(null);
    const enrollmentToDeleteId = ref(null);
    const isUpdatingEnrollment = ref(false);
    const isDeletingEnrollment = ref(false);
    const editEnrollmentForm = reactive({
      id: '',
      status: '',
      admin_notes: '',
      course_name: '',
      course_price: '',
      user_name: '',
      user_age: '',
      user_email: '',
      user_phone: '',
      message: ''
    });
    
    // 检查登录状态
    const checkAuthStatus = async () => {
      const token = localStorage.getItem('token');
      if (token) {
        try {
          const response = await fetch('https://enen.saitsuki.uk/api/auth/me', {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });
          
          if (response.ok) {
            const userData = await response.json();
            user.value = userData;
            // 获取数据
            fetchNotices();
            fetchUsers();
            fetchMessages();
            fetchAccessInfo();
          } else {
            localStorage.removeItem('token');
            user.value = null;
          }
        } catch (error) {
          console.error('认证检查失败:', error);
          localStorage.removeItem('token');
          user.value = null;
        }
      }
    };
    
    // 组件挂载时检查认证状态
    onMounted(() => {
      checkAuthStatus();
      loadAvailablePages();
    });
    
    // Course Enrollments Functions
    const fetchEnrollments = async () => {
      isLoadingEnrollments.value = true;
      enrollmentsError.value = '';
      
      try {
        const response = await getAllEnrollments();
        if (response.success) {
          enrollments.value = response.data;
        } else {
          throw new Error(response.message || 'Failed to fetch enrollments');
        }
      } catch (error) {
        console.error('申し込み情報取得エラー:', error);
        enrollmentsError.value = '申し込み情報の読み込みに失敗しました。';
      } finally {
        isLoadingEnrollments.value = false;
      }
    };
    
    const filteredEnrollments = computed(() => {
      let result = [...enrollments.value];
      
      // Status filter
      if (enrollmentsFilter.status !== 'all') {
        result = result.filter(enrollment => enrollment.status === enrollmentsFilter.status);
      }
      
      // Course filter
      if (enrollmentsFilter.course !== 'all') {
        result = result.filter(enrollment => enrollment.course_category === enrollmentsFilter.course);
      }
      
      return result;
    });
    
    const getEnrollmentStatusDisplayName = (status) => {
      return getStatusDisplayName(status);
    };
    
    const formatEnrollmentDate = (timestamp) => {
      if (!timestamp) return '日時不明';
      
      const date = new Date(timestamp);
      if (isNaN(date.getTime())) return '日時不明';
      
      return `${date.getFullYear()}/${date.getMonth() + 1}/${date.getDate()} ${date.getHours()}:${String(date.getMinutes()).padStart(2, '0')}`;
    };
    
    const editEnrollment = (enrollment) => {
      editingEnrollment.value = enrollment;
      editEnrollmentForm.id = enrollment.id;
      editEnrollmentForm.status = enrollment.status;
      editEnrollmentForm.admin_notes = enrollment.admin_notes || '';
      editEnrollmentForm.course_name = enrollment.course_name;
      editEnrollmentForm.course_price = enrollment.course_price;
      editEnrollmentForm.user_name = enrollment.user_name;
      editEnrollmentForm.user_age = enrollment.user_age;
      editEnrollmentForm.user_email = enrollment.user_email;
      editEnrollmentForm.user_phone = enrollment.user_phone;
      editEnrollmentForm.message = enrollment.message || '';
      
      showEditEnrollmentModal.value = true;
    };
    
    const saveEnrollmentEdit = async () => {
      isUpdatingEnrollment.value = true;
      
      try {
        const response = await updateEnrollmentStatusAPI(editEnrollmentForm.id, {
          status: editEnrollmentForm.status,
          admin_notes: editEnrollmentForm.admin_notes
        });
        
        if (response.success) {
          // Update local data
          const index = enrollments.value.findIndex(e => e.id === editEnrollmentForm.id);
          if (index !== -1) {
            enrollments.value[index].status = editEnrollmentForm.status;
            enrollments.value[index].admin_notes = editEnrollmentForm.admin_notes;
          }
          
          showEditEnrollmentModal.value = false;
          alert('申し込み情報を更新しました。');
        } else {
          throw new Error(response.message || 'Update failed');
        }
      } catch (error) {
        console.error('申し込み情報更新エラー:', error);
        alert('申し込み情報の更新に失敗しました: ' + error.message);
      } finally {
        isUpdatingEnrollment.value = false;
      }
    };
    
    const updateEnrollmentStatusDirect = async (enrollmentId, status) => {
      if (!confirm(`この申し込みを「${getEnrollmentStatusDisplayName(status)}」に変更しますか？`)) {
        return;
      }
      
      isUpdatingEnrollment.value = true;
      
      try {
        const response = await updateEnrollmentStatusAPI(enrollmentId, { status });
        
        if (response.success) {
          // Update local data
          const index = enrollments.value.findIndex(e => e.id === enrollmentId);
          if (index !== -1) {
            enrollments.value[index].status = status;
          }
          
          alert('ステータスを更新しました。');
        } else {
          throw new Error(response.message || 'Update failed');
        }
      } catch (error) {
        console.error('ステータス更新エラー:', error);
        alert('ステータスの更新に失敗しました: ' + error.message);
      } finally {
        isUpdatingEnrollment.value = false;
      }
    };
    
    const confirmDeleteEnrollment = (id) => {
      enrollmentToDeleteId.value = id;
      showDeleteEnrollmentModal.value = true;
    };
    
    const deleteEnrollment = async () => {
      isDeletingEnrollment.value = true;
      
      try {
        const response = await deleteEnrollmentAPI(enrollmentToDeleteId.value);
        
        if (response.success) {
          enrollments.value = enrollments.value.filter(e => e.id !== enrollmentToDeleteId.value);
          showDeleteEnrollmentModal.value = false;
          alert('申し込み情報を削除しました。');
        } else {
          throw new Error(response.message || 'Delete failed');
        }
      } catch (error) {
        console.error('申し込み削除エラー:', error);
        alert('申し込み情報の削除に失敗しました: ' + error.message);
      } finally {
        isDeletingEnrollment.value = false;
      }
    };
    
    // ログイン処理
    const login = async () => {
      isLoading.value = true;
      loginError.value = '';
      
      try {
        const response = await fetch('https://enen.saitsuki.uk/api/auth/login', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            email: loginForm.email,
            password: loginForm.password
          })
        });

        const data = await response.json();

        if (response.ok) {
          localStorage.setItem('token', data.token);
          user.value = data.user;
          // 获取数据
          fetchNotices();
          fetchUsers();
          fetchMessages();
          fetchAccessInfo();
        } else {
          loginError.value = data.message || 'ログインに失敗しました。';
        }
      } catch (error) {
        console.error('ログインエラー:', error);
        loginError.value = 'ログインに失敗しました。メールアドレスとパスワードを確認してください。';
      } finally {
        isLoading.value = false;
      }
    };
    
    // ログアウト処理
    const logout = async () => {
      try {
        await fetch('https://enen.saitsuki.uk/api/auth/logout', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
      } catch (error) {
        console.error('ログアウトエラー:', error);
      } finally {
        localStorage.removeItem('token');
        user.value = null;
        notices.value = [];
        users.value = [];
        messages.value = [];
        showAddNoticeForm.value = false;
        editingNotice.value = null;
      }
    };
    
    // お知らせ取得
    const fetchNotices = async () => {
      isLoadingNotices.value = true;
      
      try {
        const response = await fetch('https://enen.saitsuki.uk/api/notices', {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });

        if (!response.ok) {
          throw new Error('Failed to fetch notices');
        }

        const data = await response.json();
        // 后端返回的格式是 {notices: [...]}，所以需要访问 data.notices
        const noticesArray = data.notices || [];
        notices.value = noticesArray.map(notice => ({
          id: notice.id,
          title: notice.title,
          content: notice.content,
          date: notice.date,
          isImportant: notice.is_important,
          imageUrl: notice.image_url || ''
        }));
      } catch (error) {
        console.error('お知らせ取得エラー:', error);
      } finally {
        isLoadingNotices.value = false;
      }
    };
    
    // ユーザー取得
    const fetchUsers = async () => {
      isLoadingUsers.value = true;
      usersError.value = '';
      
      try {
        const response = await fetch('https://enen.saitsuki.uk/api/user-management/users', {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        
        if (response.ok) {
          const data = await response.json();
          users.value = data.data;
        } else {
          throw new Error('Failed to fetch users');
        }
      } catch (error) {
        console.error('ユーザー取得エラー:', error);
        usersError.value = 'ユーザー情報の取得に失敗しました。';
      } finally {
        isLoadingUsers.value = false;
      }
    };

    // 用户筛选计算属性
    const filteredUsers = computed(() => {
      let result = [...users.value];
      
      // 状态筛选
      if (usersFilter.status !== 'all') {
        result = result.filter(user => user.status === usersFilter.status);
      }
      
      // 搜索筛选
      if (usersFilter.search) {
        const searchTerm = usersFilter.search.toLowerCase();
        result = result.filter(user => 
          (user.name && user.name.toLowerCase().includes(searchTerm)) ||
          user.email.toLowerCase().includes(searchTerm)
        );
      }
      
      return result;
    });

    // 课程状态显示名称
    const getCourseStatusName = (status) => {
      const statusMap = {
        'pending': '審査中',
        'approved': '承認済み',
        'rejected': '拒否'
      };
      return statusMap[status] || status;
    };

    // 编辑用户
    const editUser = (user) => {
      editUserForm.id = user.id;
      editUserForm.name = user.name || '';
      editUserForm.email = user.email;
      editUserForm.phone = user.phone || '';
      showEditUserModal.value = true;
    };

    // 保存用户编辑
    const saveUserEdit = async () => {
      isUpdatingUser.value = true;
      
      try {
        const response = await fetch(`https://enen.saitsuki.uk/api/user-management/${editUserForm.id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify({
            name: editUserForm.name,
            email: editUserForm.email,
            phone: editUserForm.phone
          })
        });
        
        if (response.ok) {
          showEditUserModal.value = false;
          await fetchUsers();
        } else {
          throw new Error('Failed to update user');
        }
      } catch (error) {
        console.error('ユーザー更新エラー:', error);
        alert('ユーザー情報の更新に失敗しました。');
      } finally {
        isUpdatingUser.value = false;
      }
    };

    // 添加用户课程
    const addUserCourse = async (user) => {
      await fetchAvailableCourses(); // 确保课程数据已加载
      
      editCourseForm.id = '';
      editCourseForm.user_id = user.id;
      editCourseForm.course_name = '';
      editCourseForm.course_category = '';
      editCourseForm.status = 'approved';
      editCourseForm.notes = '';
      showEditCourseModal.value = true;
    };

    // 编辑用户课程
    const editUserCourse = async (user, course) => {
      await fetchAvailableCourses(); // 确保课程数据已加载
      
      editCourseForm.id = course.id;
      editCourseForm.user_id = user.id;
      editCourseForm.course_name = course.course_name;
      editCourseForm.course_category = course.course_category;
      editCourseForm.status = course.status;
      editCourseForm.notes = course.notes || '';
      showEditCourseModal.value = true;
    };

    // 保存用户课程编辑
    const saveUserCourseEdit = async () => {
      isUpdatingCourse.value = true;
      
      try {
        // 确保课程分类正确设置
        if (!editCourseForm.course_category) {
          const selectedCourse = availableCourses.value.find(course => course.name === editCourseForm.course_name);
          if (selectedCourse) {
            editCourseForm.course_category = selectedCourse.category;
          }
        }
        
        const url = editCourseForm.id 
          ? `https://enen.saitsuki.uk/api/user-management/courses/${editCourseForm.id}`
          : 'https://enen.saitsuki.uk/api/user-management/courses';
        
        const method = editCourseForm.id ? 'PUT' : 'POST';
        
        // 构建课程分类字符串（用于后端解析）
        let categoryString = editCourseForm.course_category;
        
        // 对于英检课程，需要添加子分类
        if (editCourseForm.course_category === 'eiken') {
          const selectedCourse = availableCourses.value.find(course => course.name === editCourseForm.course_name);
          if (selectedCourse && selectedCourse.subcategory) {
            categoryString = `${editCourseForm.course_category}:${selectedCourse.subcategory}`;
          }
        }
        
        const requestBody = {
          user_id: editCourseForm.user_id,
          course_name: editCourseForm.course_name,
          course_category: categoryString,
          status: editCourseForm.status,
          notes: editCourseForm.notes
        };
        
        console.log('Saving course with data:', requestBody);
        
        const response = await fetch(url, {
          method,
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify(requestBody)
        });
        
        if (response.ok) {
          showEditCourseModal.value = false;
          await fetchUsers();
          alert('課程保存成功！');
        } else {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to save user course');
        }
      } catch (error) {
        console.error('ユーザーコース保存エラー:', error);
        alert('ユーザーコースの保存に失敗しました: ' + error.message);
      } finally {
        isUpdatingCourse.value = false;
      }
    };

    // 删除用户课程
    const deleteUserCourse = async () => {
      if (!editCourseForm.id) return;
      
      try {
        const response = await fetch(`https://enen.saitsuki.uk/api/user-management/courses/${editCourseForm.id}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        
        if (response.ok) {
          showEditCourseModal.value = false;
          await fetchUsers();
          alert('課程削除成功！');
        } else {
          throw new Error('Failed to delete user course');
        }
      } catch (error) {
        console.error('ユーザーコース削除エラー:', error);
        alert('ユーザーコースの削除に失敗しました。');
      }
    };

    // 确认删除用户
    const confirmDeleteUser = (user) => {
      userToDelete.value = user;
      showDeleteUserModal.value = true;
    };

    // 删除用户
    const deleteUser = async () => {
      if (!userToDelete.value) return;
      
      isDeletingUser.value = true;
      
      try {
        const response = await fetch(`https://enen.saitsuki.uk/api/user-management/${userToDelete.value.id}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        
        if (response.ok) {
          showDeleteUserModal.value = false;
          userToDelete.value = null;
          await fetchUsers();
        } else {
          throw new Error('Failed to delete user');
        }
      } catch (error) {
        console.error('ユーザー削除エラー:', error);
        alert('ユーザーの削除に失敗しました。');
      } finally {
        isDeletingUser.value = false;
      }
    };

    // 封禁用户
    const banUser = (user) => {
      userToBan.value = user;
      banReason.value = '';
      showBanUserModal.value = true;
    };

    // 确认封禁用户
    const confirmBanUser = async () => {
      if (!userToBan.value || !banReason.value.trim()) {
        alert('停止理由を入力してください。');
        return;
      }
      
      isBanningUser.value = true;
      
      try {
        const response = await fetch(`https://enen.saitsuki.uk/api/user-management/ban?id=${userToBan.value.id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify({
            reason: banReason.value
          })
        });
        
        if (response.ok) {
          showBanUserModal.value = false;
          userToBan.value = null;
          banReason.value = '';
          await fetchUsers();
        } else {
          throw new Error('Failed to ban user');
        }
      } catch (error) {
        console.error('ユーザー停止エラー:', error);
        alert('ユーザーの停止に失敗しました。');
      } finally {
        isBanningUser.value = false;
      }
    };

    // 解除封禁用户
    const unbanUser = async (user) => {
      try {
        const response = await fetch(`https://enen.saitsuki.uk/api/user-management/unban?id=${user.id}`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        
        if (response.ok) {
          await fetchUsers();
        } else {
          throw new Error('Failed to unban user');
        }
      } catch (error) {
        console.error('ユーザー復活エラー:', error);
        alert('ユーザーの復活に失敗しました。');
      }
    };
    
    // 圖片上傳處理
    const handleImageChange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;

      isUploadingImage.value = true;
      noticeForm.imageUrl = '';
      uploadProgress.value = 0;

      try {
        const formData = new FormData();
        formData.append('image', file);

        const response = await fetch('https://enen.saitsuki.uk/api/upload', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: formData
        });

        if (!response.ok) {
          throw new Error('Upload failed');
        }

        const result = await response.json();
        noticeForm.imageUrl = result.url;
        uploadProgress.value = 100;
      } catch (error) {
        console.error('上傳錯誤:', error);
        alert('画像のアップロードに失敗しました。');
        uploadProgress.value = 0;
      } finally {
        isUploadingImage.value = false;
      }
    };
    
    // 編輯圖片上傳處理
    const handleEditImageChange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;

      isUploadingImage.value = true;
      const oldImageUrl = editNoticeForm.imageUrl;
      editNoticeForm.imageUrl = '';
      uploadProgress.value = 0;

      try {
        const formData = new FormData();
        formData.append('image', file);

        const response = await fetch('https://enen.saitsuki.uk/api/upload', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: formData
        });

        if (!response.ok) {
          throw new Error('Upload failed');
        }

        const result = await response.json();
        editNoticeForm.imageUrl = result.url;
        uploadProgress.value = 100;
      } catch (error) {
        console.error('上傳錯誤:', error);
        alert('画像のアップロードに失敗しました。');
        uploadProgress.value = 0;
        editNoticeForm.imageUrl = oldImageUrl;
      } finally {
        isUploadingImage.value = false;
      }
    };
    
    // 刪除圖片
    const removeImage = () => {
      if (editNoticeForm.imageUrl && !isUploadingImage.value) {
        editNoticeForm.imageUrl = '';
      }
    };
    
    // 新規お知らせ追加
    const addNotice = async () => {
      if (isUploadingImage.value) {
          alert('画像処理中です。しばらくお待ちください。');
          return;
      }
      isSubmitting.value = true;
      try {
        const response = await fetch('https://enen.saitsuki.uk/api/notices', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify({
            title: noticeForm.title,
            content: noticeForm.content,
            date: noticeForm.date,
            is_important: noticeForm.isImportant,
            image_url: noticeForm.imageUrl || ''
          })
        });

        if (!response.ok) {
          throw new Error('Failed to add notice');
        }

        noticeForm.title = '';
        noticeForm.content = '';
        noticeForm.date = new Date().toISOString().substr(0, 10);
        noticeForm.isImportant = false;
        noticeForm.imageUrl = '';
        showAddNoticeForm.value = false;
        await fetchNotices();
      } catch (error) {
        console.error('お知らせ追加エラー:', error);
        alert('お知らせの追加に失敗しました。');
      } finally {
        isSubmitting.value = false;
      }
    };
    
    // 編集モード開始
    const editNotice = (notice) => {
      editingNotice.value = notice;
      editNoticeForm.id = notice.id;
      editNoticeForm.title = notice.title;
      editNoticeForm.date = notice.date;
      editNoticeForm.content = notice.content;
      editNoticeForm.isImportant = notice.isImportant;
      editNoticeForm.imageUrl = notice.imageUrl || '';
    };
    
    // 編集キャンセル
    const cancelEdit = () => {
      editingNotice.value = null;
    };
    
    // お知らせ更新
    const updateNotice = async () => {
      if (isUploadingImage.value) {
          alert('画像処理中です。しばらくお待ちください。');
          return;
      }
      isSubmitting.value = true;
      try {
        const response = await fetch(`https://enen.saitsuki.uk/api/notices/${editNoticeForm.id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify({
            title: editNoticeForm.title,
            content: editNoticeForm.content,
            date: editNoticeForm.date,
            is_important: editNoticeForm.isImportant,
            image_url: editNoticeForm.imageUrl || ''
          })
        });

        if (!response.ok) {
          throw new Error('Failed to update notice');
        }

        editingNotice.value = null;
        await fetchNotices();
      } catch (error) {
        console.error('お知らせ更新エラー:', error);
        alert('お知らせの更新に失敗しました。');
      } finally {
        isSubmitting.value = false;
      }
    };
    
    // 削除確認
    const confirmDeleteNotice = (id) => {
      noticeToDeleteId.value = id;
      showDeleteModal.value = true;
    };
    
    // お知らせ削除
    const deleteNotice = async () => {
      isDeleting.value = true;
      
      try {
        const response = await fetch(`https://enen.saitsuki.uk/api/notices/${noticeToDeleteId.value}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });

        if (!response.ok) {
          throw new Error('Failed to delete notice');
        }
        
        // モーダルを閉じる
        showDeleteModal.value = false;
        
        // 一覧を更新
        await fetchNotices();
      } catch (error) {
        console.error('お知らせ削除エラー:', error);
        alert('お知らせの削除に失敗しました。');
      } finally {
        isDeleting.value = false;
      }
    };
    
    // お知らせ追加キャンセル
    const cancelAddNotice = () => {
      showAddNoticeForm.value = false;
      
      // フォームをリセット
      noticeForm.title = '';
      noticeForm.content = '';
      noticeForm.date = new Date().toISOString().substr(0, 10);
      noticeForm.isImportant = false;
      noticeForm.imageUrl = '';
    };
    
    // 日付フォーマット
    const formatDate = (dateStr) => {
      const date = new Date(dateStr);
      return `${date.getFullYear()}年${date.getMonth() + 1}月${date.getDate()}日`;
    };

    // Access Info Logic
    const fetchAccessInfo = async () => {
      isLoadingAccess.value = true;
      accessError.value = '';
      accessSuccess.value = '';
      try {
        const response = await fetch('https://enen.saitsuki.uk/api/settings/access', {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });

        if (response.ok) {
          const data = await response.json();
          accessData.value = {
            address: data.address || '',
            phoneNumber: data.phone_number || '',
            transportInfo: data.transport_info || '',
            googleMapsEmbedUrl: data.google_maps_embed_url || ''
          };
        } else {
          console.log("アクセス情報の取得に失敗しました。");
          accessData.value = { address: '', phoneNumber: '', transportInfo: '', googleMapsEmbedUrl: '' };
        }
      } catch (error) {
        console.error("アクセス情報の読み込み中にエラーが発生しました:", error);
        accessError.value = "アクセス情報の読み込みに失敗しました。";
      } finally {
        isLoadingAccess.value = false;
      }
    };

    const saveAccessInfo = async () => {
      isSavingAccess.value = true;
      accessError.value = '';
      accessSuccess.value = '';
      try {
        const response = await fetch('https://enen.saitsuki.uk/api/settings/access', {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify({
            address: accessData.value.address,
            phone_number: accessData.value.phoneNumber,
            transport_info: accessData.value.transportInfo,
            google_maps_embed_url: accessData.value.googleMapsEmbedUrl
          })
        });

        if (response.ok) {
          accessSuccess.value = "アクセス情報を保存しました！";
          setTimeout(() => accessSuccess.value = '', 3000);
        } else {
          const errorData = await response.json();
          accessError.value = `保存に失敗しました: ${errorData.error || 'Unknown error'}`;
        }
      } catch (error) {
        console.error("アクセス情報の保存中にエラーが発生しました:", error);
        accessError.value = `保存に失敗しました: ${error.message}`;
      } finally {
        isSavingAccess.value = false;
      }
    };

    // フィルタリングされたメッセージを取得
    const filteredMessages = computed(() => {
      let result = [...messages.value];
      
      // 未読/既読フィルター
      if (messagesFilter.readStatus === 'read') {
        result = result.filter(message => message.is_read);
      } else if (messagesFilter.readStatus === 'unread') {
        result = result.filter(message => !message.is_read);
      }
      
      // ユーザータイプフィルター
      if (messagesFilter.userType !== 'all') {
        result = result.filter(message => message.user_type === messagesFilter.userType);
      }
      
      return result;
    });

    // メッセージ取得
    const fetchMessages = async () => {
      isLoadingMessages.value = true;
      messagesError.value = '';
      
      try {
        const response = await fetch('https://enen.saitsuki.uk/api/messages', {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });

        if (response.ok) {
          const data = await response.json();
          messages.value = data.messages || [];
        } else {
          throw new Error('Failed to fetch messages');
        }
      } catch (error) {
        console.error('メッセージ取得エラー:', error);
        messagesError.value = 'メッセージの読み込みに失敗しました。';
      } finally {
        isLoadingMessages.value = false;
      }
    };
    
    // メッセージの既読/未読を切り替え
    const toggleMessageReadStatus = async (message) => {
      try {
        const response = await fetch(`https://enen.saitsuki.uk/api/messages/${message.id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify({
            is_read: !message.is_read
          })
        });

        if (response.ok) {
          message.is_read = !message.is_read;
        } else {
          throw new Error('Failed to update message status');
        }
      } catch (error) {
        console.error('メッセージ状態更新エラー:', error);
        alert('メッセージの状態更新に失敗しました。');
      }
    };
    
    // メッセージ削除確認
    const confirmDeleteMessage = (id) => {
      messageToDeleteId.value = id;
      showDeleteMessageModal.value = true;
    };
    
    // メッセージ削除
    const deleteMessage = async () => {
      isDeletingMessage.value = true;
      
      try {
        const response = await fetch(`https://enen.saitsuki.uk/api/messages/${messageToDeleteId.value}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });

        if (response.ok) {
          messages.value = messages.value.filter(m => m.id !== messageToDeleteId.value);
          showDeleteMessageModal.value = false;
        } else {
          throw new Error('Failed to delete message');
        }
      } catch (error) {
        console.error('メッセージ削除エラー:', error);
        alert('メッセージの削除に失敗しました。');
      } finally {
        isDeletingMessage.value = false;
      }
    };
    
    // メッセージ日付フォーマット
    const formatMessageDate = (timestamp) => {
      if (!timestamp) return '日時不明';
      
      const date = new Date(timestamp);
      
      if (isNaN(date.getTime())) return '日時不明';
      
      return `${date.getFullYear()}/${date.getMonth() + 1}/${date.getDate()} ${date.getHours()}:${String(date.getMinutes()).padStart(2, '0')}`;
    };

    // Page Editor Functions
    const loadAvailablePages = async () => {
      try {
        const response = await fetch('https://enen.saitsuki.uk/api/page-content/pages', {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        
        if (response.ok) {
          const data = await response.json();
          // 合并服务器返回的页面和本地定义的 header/footer 页面
          availablePages.value = {
            ...data.data,
            header: 'Header (ナビゲーション)',
            footer: 'Footer (フッター)'
          };
        } else {
          // 如果请求失败，使用默认的页面选项
          availablePages.value = {
            about: 'About (教室について)',
            courses: 'Courses (コース案内)',
            home: 'Home (ホーム)',
            header: 'Header (ナビゲーション)',
            footer: 'Footer (フッター)'
          };
        }
      } catch (error) {
        console.error('ページリスト取得エラー:', error);
        // 如果发生错误，使用默认的页面选项
        availablePages.value = {
          about: 'About (教室について)',
          courses: 'Courses (コース案内)',
          home: 'Home (ホーム)',
          header: 'Header (ナビゲーション)',
          footer: 'Footer (フッター)'
        };
      }
    };

    const loadPageContent = async () => {
      if (!selectedPage.value) return;
      
      isLoadingPageContent.value = true;
      pageContentError.value = '';
      
      try {
        const response = await fetch(`https://enen.saitsuki.uk/api/page-content/${selectedPage.value}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        
        if (response.ok) {
          const data = await response.json();
          const content = data.data;
          
          // Initialize page content with default values
          if (selectedPage.value === 'about') {
            pageContent.value = {
              welcome_title: content.welcome_title || '皆の英語教室へようこそ',
              welcome_content: content.welcome_content || '',
              welcome_image: content.welcome_image || '',
              mission_title: content.mission_title || '私たちの使命',
              mission_content: content.mission_content || '',
              features_title: content.features_title || '皆の英語教室で学ぶ、6つのポイント',
              features: content.features ? JSON.parse(content.features) : [
                { icon: 'fas fa-graduation-cap', title: '国際資格を持つ講師が指導', description: 'ITTT認定英語講師、シンガポール国立大学 免疫学講師' },
                { icon: 'fas fa-users', title: '老若男女みんなが楽しめる', description: 'どんな年齢の方も楽しめる、実践的な学び' },
                { icon: 'fas fa-user-graduate', title: '一人一人に合ったカリキュラム', description: '子どもも、お年寄りも、それぞれに合った指導' },
                { icon: 'fas fa-award', title: '英検対策もバッチリ！', description: '段階別の実力に合わせた指導で合格への近道' },
                { icon: 'fas fa-gamepad', title: '楽しく、遊びながら学ぶ', description: 'ゲーム、ロールプレイ、ストーリーテリング' },
                { icon: 'fas fa-subway', title: '駅チカ！アクセスよし', description: '御堂筋線・谷町線どちらからも！中崎町駅から徒歩2分' }
              ],
              teacher_title: content.teacher_title || '講師紹介',
              teacher_name: content.teacher_name || 'イヴリン・テオ先生',
              teacher_subtitle: content.teacher_subtitle || 'ITTT（International TEFL and TESOL Training）認定英語講師',
              teacher_description: content.teacher_description || '',
              teacher_image: content.teacher_image || '',
              gallery_title: content.gallery_title || '教室の様子',
              gallery_images: content.gallery_images ? JSON.parse(content.gallery_images) : ['', '', '', '']
            };
          } else if (selectedPage.value === 'courses') {
            pageContent.value = {
              page_title: content.page_title || 'コース紹介',
              intro_text: content.intro_text || '皆の英語教室では、年齢や目的に合わせたさまざまなコースをご用意しています。初心者から上級者まで、それぞれのレベルに合わせた指導を行っています。',
              cta_title: content.cta_title || '体験レッスン受付中！',
              cta_text: content.cta_text || 'どのコースも初回は無料で体験できます。お気軽にお問い合わせください。',
              courses_data: content.courses_data || ''
            };
            
            // Parse courses data
            if (content.courses_data) {
              try {
                const coursesArray = JSON.parse(content.courses_data);
                coursesData.value = coursesArray.map(course => ({
                  ...course,
                  target_audience_text: course.target_audience ? course.target_audience.join('\n') : ''
                }));
              } catch (e) {
                console.warn('Failed to parse courses data:', e);
                coursesData.value = getDefaultCoursesData();
              }
            } else {
              coursesData.value = getDefaultCoursesData();
            }
          } else if (selectedPage.value === 'home') {
            pageContent.value = {
              home_features_title: content.home_features_title || '皆の英語教室で学ぶ、6つのポイント',
              home_features: content.home_features ? JSON.parse(content.home_features) : [
                { icon: 'fas fa-graduation-cap', title: '国際資格を持つ講師が指導', description: 'ITTT認定英語講師、シンガポール国立大学 免疫学講師' },
                { icon: 'fas fa-users', title: '老若男女みんなが楽しめる', description: 'どんな年齢の方も楽しめる、実践的な学び' },
                { icon: 'fas fa-user-graduate', title: '一人一人に合ったカリキュラム', description: '子どもも、お年寄りも、それぞれに合った指導' },
                { icon: 'fas fa-award', title: '英検対策もバッチリ！', description: '段階別の実力に合わせた指導で合格への近道' },
                { icon: 'fas fa-gamepad', title: '楽しく、遊びながら学ぶ', description: 'ゲーム、ロールプレイ、ストーリーテリング' },
                { icon: 'fas fa-subway', title: '駅チカ！アクセスよし', description: '御堂筋線・谷町線どちらからも！中崎町駅から徒歩2分' }
              ],
              home_testimonials: content.home_testimonials ? JSON.parse(content.home_testimonials) : [
                {
                  name: '清水さん（保護者様）',
                  course: '子供向けクラス受講',
                  content: '子どもが英語を話すことに自信を持つようになりました。先生もとても親切です。',
                  image: 'q1.jpg'
                },
                {
                  name: '綿貫さん',
                  course: 'シニア向けクラス受講',
                  content: 'この教室の雰囲気が大好きです。英語を学ぶのがこんなに楽しいなんて！',
                  image: 'q2.jpg'
                }
              ],
              // Hero section
              home_hero_title: content.home_hero_title || '皆の英語教室',
              home_hero_subtitle: content.home_hero_subtitle || '楽しく、心を動かす英語教室！',
              home_hero_text: content.home_hero_text || '自信を持って英語を学ぶ。実践的なレッスンで英語力アップ！',
              home_hero_button1: content.home_hero_button1 || 'コースを見る',
              home_hero_button2: content.home_hero_button2 || 'お問い合わせ',
              home_hero_image: content.home_hero_image || '',
              // Courses section
              home_courses_title: content.home_courses_title || 'コースのご案内',
              home_courses_button: content.home_courses_button || 'もっと詳しく',
              home_courses: content.home_courses ? JSON.parse(content.home_courses) : [
                {
                  title: '子ども向け英語クラス',
                  schedule: '毎週木曜日 16:00 - 17:00 / 17:00 - 18:00',
                  description: 'みんなで物語やゲームを通して楽しく学ぶ',
                  colorClass: 'pink',
                  image: 'kids.png'
                },
                {
                  title: 'シニア向け英会話',
                  schedule: '毎週木曜日 10:00 - 11:00',
                  description: '自信を持って英語を話せるように、楽しく学びます',
                  colorClass: 'green',
                  image: 'senior.png'
                },
                {
                  title: '英検対策クラス',
                  schedule: '毎週木曜日 16:00 - 17:00 / 17:00 - 18:00',
                  description: 'ステップごとの指導で合格へ導く',
                  colorClass: 'yellow',
                  image: 'exam.png'
                }
              ],
              // CTA section
              home_cta_title: content.home_cta_title || 'まずはお気軽に体験レッスンから！',
              home_cta_text: content.home_cta_text || '4レッスン 4950円〜',
              home_cta_button: content.home_cta_button || 'お問い合わせ'
            };
          } else if (selectedPage.value === 'header') {
            pageContent.value = {
              header_logo: content.header_logo || '',
              header_title: content.header_title || '皆の英語教室',
              header_nav_home: content.header_nav_home || 'ホーム',
              header_nav_about: content.header_nav_about || '教室紹介',
              header_nav_courses: content.header_nav_courses || 'コース・料金',
              header_nav_notices: content.header_nav_notices || 'お知らせ',
              header_nav_access: content.header_nav_access || 'アクセス',
              header_nav_contact: content.header_nav_contact || 'お問い合わせ'
            };
          } else if (selectedPage.value === 'footer') {
            pageContent.value = {
              footer_logo_title: content.footer_logo_title || '皆の英語教室',
              footer_logo_subtitle: content.footer_logo_subtitle || '自信を持って英語を学ぶ。',
              footer_contact_title: content.footer_contact_title || 'お問い合わせ',
              footer_contact_address: content.footer_contact_address || '📍 中崎町ホール\n〒530-0015\n大阪府大阪市北区中崎西1丁目6-8',
              footer_contact_email: content.footer_contact_email || '✉️ ateloetti@gmail.com',
              footer_links_title: content.footer_links_title || 'クイックリンク',
              footer_links: content.footer_links ? JSON.parse(content.footer_links) : [
                { name: 'ホーム', url: '/' },
                { name: 'コース', url: '/courses' },
                { name: '教室について', url: '/about' },
                { name: 'お知らせ', url: '/notices' },
                { name: 'お問い合わせ', url: '/contact' }
              ]
            };
          }
        } else {
          throw new Error('Failed to load page content');
        }
      } catch (error) {
        console.error('ページ内容読み込みエラー:', error);
        pageContentError.value = 'ページ内容の読み込みに失敗しました。';
        
        // Set default courses data if loading fails
        if (selectedPage.value === 'courses') {
          coursesData.value = getDefaultCoursesData();
        } else if (selectedPage.value === 'home') {
          // Set default home data if loading fails
          pageContent.value = {
            home_features_title: '皆の英語教室で学ぶ、6つのポイント',
            home_features: [
              { icon: 'fas fa-graduation-cap', title: '国際資格を持つ講師が指導', description: 'ITTT認定英語講師、シンガポール国立大学 免疫学講師' },
              { icon: 'fas fa-users', title: '老若男女みんなが楽しめる', description: 'どんな年齢の方も楽しめる、実践的な学び' },
              { icon: 'fas fa-user-graduate', title: '一人一人に合ったカリキュラム', description: '子どもも、お年寄りも、それぞれに合った指導' },
              { icon: 'fas fa-award', title: '英検対策もバッチリ！', description: '段階別の実力に合わせた指導で合格への近道' },
              { icon: 'fas fa-gamepad', title: '楽しく、遊びながら学ぶ', description: 'ゲーム、ロールプレイ、ストーリーテリング' },
              { icon: 'fas fa-subway', title: '駅チカ！アクセスよし', description: '御堂筋線・谷町線どちらからも！中崎町駅から徒歩2分' }
            ],
            home_testimonials: [
              {
                name: '清水さん（保護者様）',
                course: '子供向けクラス受講',
                content: '子どもが英語を話すことに自信を持つようになりました。先生もとても親切です。',
                image: 'q1.jpg'
              },
              {
                name: '綿貫さん',
                course: 'シニア向けクラス受講',
                content: 'この教室の雰囲気が大好きです。英語を学ぶのがこんなに楽しいなんて！',
                image: 'q2.jpg'
              }
            ],
            // Hero section
            home_hero_title: '皆の英語教室',
            home_hero_subtitle: '楽しく、心を動かす英語教室！',
            home_hero_text: '自信を持って英語を学ぶ。実践的なレッスンで英語力アップ！',
            home_hero_button1: 'コースを見る',
            home_hero_button2: 'お問い合わせ',
            home_hero_image: '',
            // Courses section
            home_courses_title: 'コースのご案内',
            home_courses_button: 'もっと詳しく',
            home_courses: [
              {
                title: '子ども向け英語クラス',
                schedule: '毎週木曜日 16:00 - 17:00 / 17:00 - 18:00',
                description: 'みんなで物語やゲームを通して楽しく学ぶ',
                colorClass: 'pink',
                image: 'kids.png'
              },
              {
                title: 'シニア向け英会話',
                schedule: '毎週木曜日 10:00 - 11:00',
                description: '自信を持って英語を話せるように、楽しく学びます',
                colorClass: 'green',
                image: 'senior.png'
              },
              {
                title: '英検対策クラス',
                schedule: '毎週木曜日 16:00 - 17:00 / 17:00 - 18:00',
                description: 'ステップごとの指導で合格へ導く',
                colorClass: 'yellow',
                image: 'exam.png'
              }
            ],
            // CTA section
            home_cta_title: 'まずはお気軽に体験レッスンから！',
            home_cta_text: '4レッスン 4950円〜',
            home_cta_button: 'お問い合わせ'
          };
        } else if (selectedPage.value === 'header') {
          pageContent.value = {
            header_logo: '',
            header_title: '皆の英語教室',
            header_nav_home: 'ホーム',
            header_nav_about: '教室紹介',
            header_nav_courses: 'コース・料金',
            header_nav_notices: 'お知らせ',
            header_nav_access: 'アクセス',
            header_nav_contact: 'お問い合わせ'
          };
        } else if (selectedPage.value === 'footer') {
          pageContent.value = {
            footer_logo_title: '皆の英語教室',
            footer_logo_subtitle: '自信を持って英語を学ぶ。',
            footer_contact_title: 'お問い合わせ',
            footer_contact_address: '📍 中崎町ホール\n〒530-0015\n大阪府大阪市北区中崎西1丁目6-8',
            footer_contact_email: '✉️ ateloetti@gmail.com',
            footer_links_title: 'クイックリンク',
            footer_links: [
              { name: 'ホーム', url: '/' },
              { name: 'コース', url: '/courses' },
              { name: '教室について', url: '/about' },
              { name: 'お知らせ', url: '/notices' },
              { name: 'お問い合わせ', url: '/contact' }
            ]
          };
        }
      } finally {
        isLoadingPageContent.value = false;
      }
    };

    const savePageContent = async () => {
      isSavingPageContent.value = true;
      pageContentError.value = '';
      pageContentSuccess.value = '';
      
      try {
        const contentToSave = { ...pageContent.value };
        
        // Convert arrays to JSON strings for storage
        if (selectedPage.value === 'about') {
          if (contentToSave.features) {
            contentToSave.features = JSON.stringify(contentToSave.features);
          }
          if (contentToSave.gallery_images) {
            contentToSave.gallery_images = JSON.stringify(contentToSave.gallery_images);
          }
        } else if (selectedPage.value === 'courses') {
          // Prepare courses data for saving
          const coursesForSave = coursesData.value.map(course => ({
            id: course.id,
            title: course.title,
            image: course.image,
            schedule: course.schedule,
            description: course.description,
            target_audience: course.target_audience_text ? 
              course.target_audience_text.split('\n').filter(item => item.trim()) : [],
            price: course.price
          }));
          contentToSave.courses_data = JSON.stringify(coursesForSave);
        } else if (selectedPage.value === 'home') {
          // Prepare home data for saving
          if (contentToSave.home_features) {
            contentToSave.home_features = JSON.stringify(contentToSave.home_features);
          }
          if (contentToSave.home_testimonials) {
            contentToSave.home_testimonials = JSON.stringify(contentToSave.home_testimonials);
          }
          if (contentToSave.home_courses) {
            contentToSave.home_courses = JSON.stringify(contentToSave.home_courses);
          }
        } else if (selectedPage.value === 'footer') {
          // Prepare footer data for saving
          if (contentToSave.footer_links) {
            contentToSave.footer_links = JSON.stringify(contentToSave.footer_links);
          }
        }
        
        const response = await fetch(`https://enen.saitsuki.uk/api/page-content/${selectedPage.value}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify(contentToSave)
        });
        
        if (response.ok) {
          pageContentSuccess.value = 'ページ内容を保存しました！';
          setTimeout(() => pageContentSuccess.value = '', 3000);
        } else {
          throw new Error('Failed to save page content');
        }
      } catch (error) {
        console.error('ページ内容保存エラー:', error);
        pageContentError.value = 'ページ内容の保存に失敗しました。';
      } finally {
        isSavingPageContent.value = false;
      }
    };

    // Image upload handlers for page editor
    const handleWelcomeImageChange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;
      
      await uploadPageImage(file, 'welcome_image');
    };

    const handleTeacherImageChange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;
      
      await uploadPageImage(file, 'teacher_image');
    };

    const handleGalleryImageChange = async (e, index) => {
      const file = e.target.files[0];
      if (!file) return;
      
      await uploadPageImage(file, 'gallery_images', index);
    };

    const uploadPageImage = async (file, field, index = null) => {
      isUploadingPageImage.value = true;
      pageImageUploadProgress.value = 0;
      
      try {
        const formData = new FormData();
        formData.append('image', file);
        
        const response = await fetch('https://enen.saitsuki.uk/api/upload', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: formData
        });
        
        if (!response.ok) {
          throw new Error('Upload failed');
        }
        
        const result = await response.json();
        
        if (field === 'gallery_images' && index !== null) {
          pageContent.value.gallery_images[index] = result.url;
        } else if (field === 'course_image' && index !== null) {
          // Handle course image upload
          coursesData.value[index].image = result.url;
        } else if (field === 'testimonial_image' && index !== null) {
          // Handle testimonial image upload
          pageContent.value.home_testimonials[index].image = result.url;
        } else {
          pageContent.value[field] = result.url;
        }
        
        pageImageUploadProgress.value = 100;
      } catch (error) {
        console.error('画像アップロードエラー:', error);
        alert('画像のアップロードに失敗しました。');
        pageImageUploadProgress.value = 0;
      } finally {
        isUploadingPageImage.value = false;
      }
    };

    const removeWelcomeImage = () => {
      pageContent.value.welcome_image = '';
    };

    const removeTeacherImage = () => {
      pageContent.value.teacher_image = '';
    };

    const removeGalleryImage = (index) => {
      pageContent.value.gallery_images[index] = '';
    };

    // Courses management methods
    const getDefaultCoursesData = () => [
      {
        id: 1,
        title: '子ども向け英語クラス',
        image: '@/assets/course/kids.png',
        schedule: '毎週木曜日 16:00 - 17:00 / 17:00 - 18:00',
        description: '遊びながら英語を学ぶ、子どものための英会話クラスです。ゲームやアクティビティーを通して、楽しく英語に触れることができます。\n\n歌やダンス、ストーリーテリングなど、子どもが夢中になれる活動を通して、自然と英語が身につきます。',
        target_audience_text: '初めて英語を学ぶお子様\n英語に興味のあるお子様\n学校の英語の授業が苦手なお子様',
        price: '<strong>料金</strong>: 月額 5,200円（月4回）'
      },
      {
        id: 2,
        title: 'シニア向け英会話',
        image: '@/assets/course/senior.png',
        schedule: '毎週木曜日 10:00 - 11:00',
        description: '旅行や趣味に役立つ、実践的な英会話を学びます。初心者からでも安心して参加できるクラスです。\n\n日常会話や旅行で使える表現を中心に、リラックスした雰囲気で学べます。同年代の仲間と一緒に学ぶことで、楽しく継続できます。',
        target_audience_text: '海外旅行を楽しみたい方\n趣味として英語を学びたい方\n脳トレとして言語学習をしたい方',
        price: '<strong>料金</strong>: 月2回x2ヶ月5200円'
      },
      {
        id: 3,
        title: '英検対策クラス',
        image: '@/assets/course/exam.png',
        schedule: '毎週木曜日 16:00 - 17:00 / 17:00 - 18:00',
        description: '英検合格に向けた対策クラスです。各級に合わせたカリキュラムで、効率的に学習できます。\n\n英検対策のプロが、リスニング、リーディング、ライティング、スピーキングをバランスよく指導します。過去問題の解説や模擬試験も実施します。',
        target_audience_text: '',
        price: '<strong>料金</strong>:\n5級以上の方 5500円\n4級以上の方 6500円\n3級以上の方 7000円\n準2級以上の方 7500円\n準2級プラス以上の方 8500円\n2級以上の方 9000円\n\n当教室の英検合格率は95%以上！確かな実績があります。'
      }
    ];

    const addCourse = () => {
      coursesData.value.push(defaultCourseData());
    };

    const removeCourse = (index) => {
      if (coursesData.value.length > 1) {
        coursesData.value.splice(index, 1);
      }
    };

    const updateTargetAudience = (course, event) => {
      course.target_audience_text = event.target.value;
      course.target_audience = event.target.value ? 
        event.target.value.split('\n').filter(item => item.trim()) : [];
    };

    // Course image upload handlers
    const handleCourseImageChange = async (e, index) => {
      const file = e.target.files[0];
      if (!file) return;
      
      await uploadPageImage(file, 'course_image', index);
    };

    const removeCourseImage = (index) => {
      if (!isUploadingPageImage.value) {
        coursesData.value[index].image = '';
      }
    };

    // Testimonial management methods
    const addTestimonial = () => {
      pageContent.value.home_testimonials.push({
        name: '',
        course: '',
        content: '',
        image: ''
      });
    };

    const removeTestimonial = (index) => {
      if (pageContent.value.home_testimonials.length > 1) {
        pageContent.value.home_testimonials.splice(index, 1);
      }
    };

    const handleTestimonialImageChange = async (e, index) => {
      const file = e.target.files[0];
      if (!file) return;
      
      await uploadPageImage(file, 'testimonial_image', index);
    };

    const removeTestimonialImage = (index) => {
      if (!isUploadingPageImage.value) {
        pageContent.value.home_testimonials[index].image = '';
      }
    };

    const getTestimonialImageUrl = (imageName) => {
      if (!imageName) return require('@/assets/q1.jpg');
      
      // Check if it's a full URL (uploaded image)
      if (imageName.startsWith('http')) {
        return imageName;
      }
      
      // Default assets
      try {
        return require(`@/assets/${imageName}`);
      } catch {
        return require('@/assets/q1.jpg');
      }
    };

    const isDefaultImage = (imageName) => {
      return !imageName || imageName === 'q1.jpg' || imageName === 'q2.jpg';
    };

    // Hero image management
    const handleHeroImageChange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;
      
      await uploadPageImage(file, 'home_hero_image');
    };

    const removeHeroImage = () => {
      if (!isUploadingPageImage.value) {
        pageContent.value.home_hero_image = '';
      }
    };

    // Header logo management
    const handleHeaderLogoChange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;
      
      await uploadPageImage(file, 'header_logo');
    };

    const removeHeaderLogo = () => {
      if (!isUploadingPageImage.value) {
        pageContent.value.header_logo = '';
      }
    };

    // Footer links management
    const addFooterLink = () => {
      pageContent.value.footer_links.push({
        name: '',
        url: ''
      });
    };

    const removeFooterLink = (index) => {
      if (pageContent.value.footer_links.length > 1) {
        pageContent.value.footer_links.splice(index, 1);
      }
    };

    // Home courses management
    const addHomeCourse = () => {
      pageContent.value.home_courses.push({
        title: '',
        schedule: '',
        description: '',
        colorClass: 'pink',
        image: ''
      });
    };

    const removeHomeCourse = (index) => {
      if (pageContent.value.home_courses.length > 1) {
        pageContent.value.home_courses.splice(index, 1);
      }
    };

    // 扩展的用户管理状态
    const usersFilter = reactive({
      status: 'all',
      search: ''
    });
    
    // 用户编辑状态
    const showEditUserModal = ref(false);
    const editUserForm = reactive({
      id: '',
      name: '',
      email: '',
      phone: ''
    });
    const isUpdatingUser = ref(false);
    
    // 用户课程管理状态
    const showEditCourseModal = ref(false);
    const editCourseForm = reactive({
      id: '',
      user_id: '',
      course_name: '',
      course_category: '',
      status: 'active',
      notes: ''
    });
    const isUpdatingCourse = ref(false);
    
    // 用户删除状态
    const showDeleteUserModal = ref(false);
    const userToDelete = ref(null);
    const isDeletingUser = ref(false);
    
    // 用户封禁状态
    const showBanUserModal = ref(false);
    const userToBan = ref(null);
    const banReason = ref('');
    const isBanningUser = ref(false);

    // 可用课程状态
    const availableCourses = ref([]);
    const isLoadingCourses = ref(false);

    // 获取可用课程列表
    const fetchAvailableCourses = async () => {
      if (availableCourses.value.length > 0) return; // 如果已经加载过，不重复请求
      
      isLoadingCourses.value = true;
      try {
        const response = await fetch('https://enen.saitsuki.uk/api/course-enrollment/courses', {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        
        if (response.ok) {
          const result = await response.json();
          if (result.success) {
            availableCourses.value = result.data;
          }
        }
      } catch (error) {
        console.error('获取课程列表失败:', error);
      } finally {
        isLoadingCourses.value = false;
      }
    };

    // 获取课程子分类名称
    const getCourseSubcategoryName = (subcategory) => {
      const subcategoryNames = {
        '5kyu': '5級',
        '4kyu': '4級', 
        '3kyu': '3級',
        'jun2kyu': '準2級',
        'jun2kyu_plus': '準2級プラス',
        '2kyu': '2級'
      };
      return subcategoryNames[subcategory] || subcategory;
    };

    // 当课程名称改变时自动设置分类
    const onCourseNameChange = (event) => {
      const selectedOption = event.target.selectedOptions[0];
      if (selectedOption) {
        const category = selectedOption.getAttribute('data-category');
        
        if (category) {
          editCourseForm.course_category = category;
        }
      }
    };

    return {
      // 認証関連
      user,
      loginForm,
      isLoading,
      loginError,
      login,
      logout,
      
      // タブ関連
      activeTab,
      
      // お知らせ関連
      notices,
      isLoadingNotices,
      noticeForm,
      showAddNoticeForm,
      isSubmitting,
      addNotice,
      cancelAddNotice,
      
      // 編集関連
      editingNotice,
      editNoticeForm,
      editNotice,
      cancelEdit,
      updateNotice,
      
      // 削除関連
      showDeleteModal,
      isDeleting,
      confirmDeleteNotice,
      deleteNotice,
      
      // ユーザー関連
      users,
      isLoadingUsers,
      usersError,
      fetchUsers,
      
      // ユーティリティ
      formatDate,
      
      uploadProgress,
      handleImageChange,
      handleEditImageChange,
      removeImage,
      isUploadingImage,

      // Access Info
      accessData,
      isLoadingAccess,
      isSavingAccess,
      accessError,
      accessSuccess,
      fetchAccessInfo,
      saveAccessInfo,
      
      // Messages
      messages,
      isLoadingMessages,
      messagesError,
      messagesFilter,
      filteredMessages,
      fetchMessages,
      toggleMessageReadStatus,
      confirmDeleteMessage,
      deleteMessage,
      showDeleteMessageModal,
      isDeletingMessage,
      formatMessageDate,

      // Page Editor
      selectedPage,
      availablePages,
      pageContent,
      isLoadingPageContent,
      pageContentError,
      pageImageUploadProgress,
      isUploadingPageImage,
      pageContentSuccess,
      isSavingPageContent,
      loadAvailablePages,
      loadPageContent,
      savePageContent,
      handleWelcomeImageChange,
      handleTeacherImageChange,
      handleGalleryImageChange,
      uploadPageImage,
      removeWelcomeImage,
      removeTeacherImage,
      removeGalleryImage,

      // Courses management
      coursesData,
      getDefaultCoursesData,
      addCourse,
      removeCourse,
      updateTargetAudience,
      handleCourseImageChange,
      removeCourseImage,
      
      // Course Enrollments
      enrollments,
      isLoadingEnrollments,
      enrollmentsError,
      enrollmentsFilter,
      filteredEnrollments,
      showEditEnrollmentModal,
      showDeleteEnrollmentModal,
      editingEnrollment,
      enrollmentToDeleteId,
      isUpdatingEnrollment,
      isDeletingEnrollment,
      editEnrollmentForm,
      fetchEnrollments,
      getEnrollmentStatusDisplayName,
      formatEnrollmentDate,
      editEnrollment,
      saveEnrollmentEdit,
      updateEnrollmentStatus: updateEnrollmentStatusDirect,
      confirmDeleteEnrollment,
      deleteEnrollment,

      // Testimonial management
      addTestimonial,
      removeTestimonial,
      handleTestimonialImageChange,
      removeTestimonialImage,
      getTestimonialImageUrl,
      isDefaultImage,

      // Home Page Editor
      addHomeCourse,
      removeHomeCourse,
      handleHeroImageChange,
      removeHeroImage,

      // Header Page Editor
      handleHeaderLogoChange,
      removeHeaderLogo,

      // Footer Page Editor
      addFooterLink,
      removeFooterLink,

      // User Management
      usersFilter,
      filteredUsers,
      getCourseStatusName,
      editUser,
      saveUserEdit,
      addUserCourse,
      editUserCourse,
      saveUserCourseEdit,
      deleteUserCourse,
      confirmDeleteUser,
      deleteUser,
      banUser,
      confirmBanUser,
      unbanUser,
      showEditUserModal,
      editUserForm,
      isUpdatingUser,
      showEditCourseModal,
      editCourseForm,
      isUpdatingCourse,
      showDeleteUserModal,
      userToDelete,
      isDeletingUser,
      showBanUserModal,
      userToBan,
      banReason,
      isBanningUser,

      // 可用课程状态
      availableCourses,
      isLoadingCourses,
      fetchAvailableCourses,
      getCourseSubcategoryName,
      onCourseNameChange
    };
  }
};
</script>

<style scoped>
.admin-page {
  min-height: 100vh;
  background-color: #f5f5f5;
}

.page-header {
  background-color: var(--primary-color);
  color: white;
  padding: 40px 0;
  text-align: center;
}

.page-title {
  font-size: 28px;
  font-weight: bold;
}

.section {
  padding: 40px 0;
}

/* ログインフォーム */
.auth-form {
  max-width: 400px;
  margin: 0 auto;
  background-color: white;
  border-radius: 10px;
  padding: 30px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
}

.form-title {
  text-align: center;
  margin-bottom: 20px;
  color: var(--primary-color);
}

.form-group {
  margin-bottom: 20px;
}

.form-group label {
  display: block;
  margin-bottom: 5px;
  font-weight: 500;
  color: #333;
}

.form-group input {
  width: 100%;
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 16px;
}

.form-check {
  display: flex;
  align-items: center;
  margin-bottom: 20px;
}

.form-check input {
  margin-right: 8px;
}

.error-message {
  background-color: #ffebee;
  color: #c62828;
  padding: 10px;
  border-radius: 4px;
  margin-bottom: 20px;
  font-size: 14px;
}

.btn {
  display: inline-block;
  padding: 10px 20px;
  border-radius: 4px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  text-align: center;
  border: none;
  font-size: 16px;
}

.btn-primary {
  background-color: var(--primary-color);
  color: white;
  width: 100%;
}

.btn-primary:hover {
  background-color: var(--secondary-color);
}

.btn-primary:disabled {
  background-color: #ccc;
  cursor: not-allowed;
}

/* ダッシュボード */
.admin-dashboard {
  background-color: white;
  border-radius: 10px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  padding: 30px;
  margin-bottom: 20px;
}

.dashboard-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
}

.welcome-text {
  margin: 0;
  color: var(--primary-color);
}

.btn-outline {
  background-color: transparent;
  color: var(--primary-color);
  border: 1px solid var(--primary-color);
}

.btn-outline:hover {
  background-color: var(--primary-color);
  color: white;
}

/* タブ */
.admin-tabs {
  display: flex;
  border-bottom: 2px solid #eee;
  margin-bottom: 30px;
}

.tab-btn {
  padding: 12px 25px;
  background: none;
  border: none;
  border-bottom: 3px solid transparent;
  cursor: pointer;
  font-size: 16px;
  color: #666;
  transition: all 0.3s ease;
  margin-bottom: -2px; /* Overlap border */
  display: flex;
  align-items: center;
  gap: 8px; /* Space between icon and text */
}

.tab-btn i {
  font-size: 18px; /* Adjust icon size */
}

.tab-btn.active {
  color: var(--primary-color);
  border-bottom-color: var(--primary-color);
  font-weight: 600;
}

.tab-btn:hover:not(.active) {
  color: #333;
  background-color: #f9f9f9;
}

.tab-content {
  /* Styles for the content area if needed */
}

.tab-pane {
 /* Add padding or margin if needed */
  padding-top: 10px;
}

/* お知らせ管理 */
.notices-management {
  margin-top: 20px;
}

.section-subtitle {
  margin-bottom: 20px;
  color: #333;
}

.add-btn {
  margin-bottom: 20px;
  display: flex;
  align-items: center;
}

.add-btn i {
  margin-right: 8px;
}

.btn-success {
  background-color: #4caf50;
  color: white;
}

.btn-success:hover {
  background-color: #388e3c;
}

/* お知らせフォーム */
.notice-form {
  background-color: #f9f9f9;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 30px;
  border: 1px solid #ddd;
}

.notice-form h4 {
  margin-top: 0;
  margin-bottom: 20px;
  color: #333;
}

.notice-form textarea {
  width: 100%;
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 4px;
  resize: vertical;
  font-family: inherit;
  font-size: 16px;
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 20px;
}

/* お知らせ一覧 */
.notices-list-admin {
  margin-top: 30px;
}

.loading-state, .empty-state {
  text-align: center;
  padding: 40px 0;
  color: #666;
}

.notice-items {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.notice-item {
  background-color: #f9f9f9;
  border-radius: 8px;
  padding: 15px;
  border: 1px solid #ddd;
}

.notice-item-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}

.notice-item-title {
  font-weight: 500;
  font-size: 18px;
  color: #333;
}

.important-badge {
  display: inline-block;
  background-color: #ff6b6b;
  color: white;
  font-size: 12px;
  padding: 2px 8px;
  border-radius: 10px;
  margin-left: 8px;
}

.notice-item-date {
  color: #666;
  font-size: 14px;
}

.notice-item-content {
  margin-bottom: 15px;
  color: #555;
  line-height: 1.5;
}

.notice-item-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

.btn-sm {
  padding: 6px 12px;
  font-size: 14px;
}

.btn-danger {
  background-color: #f44336;
  color: white;
}

.btn-danger:hover {
  background-color: #d32f2f;
}

.btn-danger:disabled {
  background-color: #ffcdd2;
  cursor: not-allowed;
}

/* 削除確認モーダル */
.delete-modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.delete-modal-content {
  background-color: white;
  border-radius: 8px;
  padding: 20px;
  width: 90%;
  max-width: 500px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);
}

.delete-modal-content h4 {
  margin-top: 0;
  color: #f44336;
}

.delete-modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 20px;
}

/* 画像プレビュー */
.image-preview {
  margin-top: 10px;
  text-align: center;
}

.image-preview img {
  max-width: 100%;
  max-height: 200px;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.progress {
  margin-top: 10px;
  height: 20px;
  background-color: #f5f5f5;
  border-radius: 4px;
  overflow: hidden;
  position: relative;
}

.progress-bar {
  height: 100%;
  background-color: var(--primary-color);
  transition: width 0.3s ease;
}

.progress span {
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  color: #333;
  font-size: 12px;
  font-weight: bold;
}

/* ユーザー管理 */
.users-management {
  margin-top: 20px;
}

.users-filter {
  display: flex;
  gap: 15px;
  margin-bottom: 20px;
  align-items: center;
  background-color: #f5f5f5;
  padding: 15px;
  border-radius: 8px;
}

.filter-item {
  display: flex;
  align-items: center;
  gap: 8px;
}

.filter-item label {
  font-weight: 500;
  color: #333;
  white-space: nowrap;
}

.filter-select, .filter-input {
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
}

.filter-input {
  min-width: 200px;
}

.users-list {
  display: flex;
  flex-direction: column;
  gap: 15px;
  margin-top: 20px;
}

.user-item {
  background-color: white;
  border: 1px solid #ddd;
  border-radius: 8px;
  padding: 20px;
  transition: all 0.3s ease;
}

.user-item:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.user-item.user-banned {
  border-left: 4px solid #ff9800;
  background-color: #fff8f0;
}

.user-header {
  display: flex;
  justify-content: space-between;
  margin-bottom: 15px;
}

.user-basic-info {
  flex: 1;
}

.user-name {
  font-size: 18px;
  font-weight: 600;
  color: #333;
  margin-bottom: 5px;
  display: flex;
  align-items: center;
  gap: 10px;
}

.user-email {
  color: #555;
  margin-bottom: 5px;
}

.user-phone {
  color: #777;
  font-size: 14px;
}

.user-status-info {
  text-align: right;
  min-width: 200px;
}

.last-login-time {
  font-size: 13px;
  color: #666;
  margin-bottom: 3px;
}

.last-login-ip {
  font-size: 12px;
  color: #888;
  font-family: monospace;
}

.banned-badge {
  background-color: #ff9800;
  color: white;
  font-size: 11px;
  padding: 3px 8px;
  border-radius: 4px;
  font-weight: normal;
}

.user-courses {
  margin-bottom: 15px;
  padding: 15px;
  background-color: #f9f9f9;
  border-radius: 6px;
}

.courses-title {
  font-weight: 500;
  margin-bottom: 10px;
  color: #333;
}

.courses-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.course-tag {
  background-color: #e3f2fd;
  color: #1976d2;
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 13px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.course-tag.course-active {
  background-color: #e8f5e9;
  color: #2e7d32;
}

.course-tag.course-completed {
  background-color: #f3e5f5;
  color: #7b1fa2;
}

.course-tag.course-cancelled {
  background-color: #ffebee;
  color: #c62828;
}

.course-status {
  font-size: 11px;
  opacity: 0.8;
}

.btn-edit-course {
  background: none;
  border: none;
  color: #666;
  cursor: pointer;
  padding: 2px;
  border-radius: 3px;
  transition: all 0.2s ease;
}

.btn-edit-course:hover {
  background-color: rgba(0, 0, 0, 0.1);
  color: #333;
}

.no-courses {
  color: #888;
  font-style: italic;
  font-size: 14px;
}

.user-actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.btn-sm {
  padding: 6px 12px;
  font-size: 13px;
  border-radius: 4px;
  border: none;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 5px;
}

.btn-sm:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-sm.btn-primary {
  background-color: #1976d2;
  color: white;
}

.btn-sm.btn-primary:hover {
  background-color: #1565c0;
}

.btn-sm.btn-success {
  background-color: #2e7d32;
  color: white;
}

.btn-sm.btn-success:hover {
  background-color: #1b5e20;
}

.btn-sm.btn-warning {
  background-color: #ff9800;
  color: white;
}

.btn-sm.btn-warning:hover {
  background-color: #f57c00;
}

.btn-sm.btn-danger {
  background-color: #d32f2f;
  color: white;
}

.btn-sm.btn-danger:hover {
  background-color: #c62828;
}

/* 模态框样式 */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-content {
  background-color: white;
  border-radius: 8px;
  padding: 30px;
  max-width: 500px;
  width: 90%;
  max-height: 80vh;
  overflow-y: auto;
}

.modal-content h4 {
  margin-top: 0;
  margin-bottom: 20px;
  color: #333;
}

.modal-actions {
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  margin-top: 20px;
}

.warning-text {
  color: #d32f2f;
  font-size: 14px;
  margin: 10px 0;
}

.user-edit-modal .form-group {
  margin-bottom: 15px;
}

.course-edit-modal .form-group {
  margin-bottom: 15px;
}

.course-edit-modal select {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
}

.course-edit-modal textarea {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  resize: vertical;
  font-family: inherit;
}

/* Footer Page Editor */
.footer-page-editor {
  margin-top: 20px;
}

.footer-logo-section {
  margin-bottom: 30px;
  padding: 20px;
  background-color: #f9f9f9;
  border-radius: 8px;
}

.footer-logo-section h5 {
  margin-bottom: 15px;
  color: #333;
}

.footer-logo-input {
  width: 100%;
  padding: 10px;
  margin-bottom: 15px;
  border: 1px solid #ddd;
  border-radius: 4px;
}

.footer-contact-section {
  margin-bottom: 30px;
  padding: 20px;
  background-color: #f9f9f9;
  border-radius: 8px;
}

.footer-contact-section h5 {
  margin-bottom: 15px;
  color: #333;
}

.footer-contact-input {
  width: 100%;
  padding: 10px;
  margin-bottom: 15px;
  border: 1px solid #ddd;
  border-radius: 4px;
}

.footer-links-section {
  margin-bottom: 30px;
  padding: 20px;
  background-color: #f9f9f9;
  border-radius: 8px;
}

.footer-links-section h5 {
  margin-bottom: 15px;
  color: #333;
}

.footer-links-input {
  width: 100%;
  padding: 10px;
  margin-bottom: 15px;
  border: 1px solid #ddd;
  border-radius: 4px;
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 20px;
}

/* Links Editor Styles */
.links-editor {
  margin-top: 20px;
}

.link-item-editor {
  background-color: white;
  border: 2px solid #e0e0e0;
  border-radius: 10px;
  padding: 25px;
  margin-bottom: 25px;
  transition: all 0.3s ease;
}

.link-item-editor:hover {
  border-color: var(--primary-color);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.link-editor-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 15px;
  border-bottom: 2px solid #f0f0f0;
}

.link-editor-header h6 {
  margin: 0;
  color: var(--primary-color);
  font-size: 18px;
  font-weight: 600;
}

.add-link-btn {
  width: 100%;
  padding: 15px;
  font-size: 16px;
  font-weight: 600;
  border: 2px dashed var(--primary-color);
  background-color: rgba(var(--primary-color-rgb), 0.05);
  color: var(--primary-color);
  border-radius: 8px;
  transition: all 0.3s ease;
}

.add-link-btn:hover {
  background-color: var(--primary-color);
  color: white;
  border-style: solid;
}

.add-link-btn i {
  margin-right: 8px;
}

/* Copyright info styles */
.copyright-info {
  background-color: #f8f9fa;
  padding: 15px;
  border-radius: 6px;
  border-left: 3px solid #6c757d;
}
</style> 